import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Table, Button, Modal, Select, Upload, Switch, message, Card, Spin, Tag, Checkbox, Input, List, Empty, Dropdown, Menu, Form, DatePicker } from 'antd';
import dayjs from 'dayjs';
import { getAuthHeaders, getAuthToken, getAuthHeadersForUpload } from '../utils/authHeaders';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { PlusOutlined, UploadOutlined, DeleteOutlined, ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined,CaretRightOutlined, ExclamationCircleOutlined, ClearOutlined, FileTextOutlined, SettingOutlined, EditOutlined, MoreOutlined, ReloadOutlined, TruckOutlined, SyncOutlined } from '@ant-design/icons';
import { ProductionQuantityLimits as ProductionQuantityLimitsIcon } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { FileClock, FileCheck, CircleX, Loader } from 'lucide-react';
import type { ColumnsType } from 'antd/es/table';
import OrdersSmartSearch from '../components/OrdersSmartSearch';
import '../styles/InsurancePage.css';
const BACKEND_URL = '/api/demographics';

const { Option } = Select;

// Sortable Item Component
interface SortableItemProps {
  id: string;
  label: string;
  checked: boolean;
  onToggle: (key: string, checked: boolean) => void;
}

const SortableItem: React.FC<SortableItemProps> = ({ id, label, checked, onToggle }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        padding: '4px 8px',
        borderRadius: '4px',
        backgroundColor: isDragging ? '#f0f0f0' : 'transparent',
        border: isDragging ? '1px solid #d9d9d9' : '1px solid transparent',
        transition: 'all 0.2s ease',
      }}
    >
      <div 
        {...attributes}
        {...listeners}
        style={{ 
          marginRight: '8px',
          cursor: 'grab',
          color: '#8c8c8c',
          fontSize: '14px',
          padding: '4px',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '20px',
          height: '20px',
          backgroundColor: '#f5f5f5',
          border: '1px solid #e8e8e8'
        }}
      >
        ⋮⋮
      </div>
      <Checkbox
        checked={checked}
        onChange={(e) => {
          e.stopPropagation();
          onToggle(id, e.target.checked);
        }}
        style={{ marginRight: '8px' }}
        onClick={(e) => e.stopPropagation()}
      />
      <span 
        style={{ 
          fontSize: '13px',
          color: '#262626',
          cursor: 'pointer',
          userSelect: 'none',
          flex: 1
        }}
        onClick={(e) => {
          e.stopPropagation();
          onToggle(id, !checked);
        }}
      >
        {label}
      </span>
    </div>
  );
};

// Auth headers are now imported from utils/authHeaders

interface DemographicsData {
  id: string;
  doctorName: string;
  procedureName: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  dateOfBirth?: string;
  surgeryDate: string;
  uploadedOn: string;
  lastModified: string;
  filename: string;
  contentType: string;
  onDemand: boolean;
  status: 'Pending' | 'In Progress' | 'Accepted' | 'Rejected' | 'Ready to Order' | 'Delivered' | 'Cancelled' | 'Ready to Bill' | 'Payment Due' | 'Payment Denied' | 'Payment Received';
  isDuplicate: boolean;
  rejectedBy?: string;
  rejectedReason?: string;
  orderId?: string; // For order-related records
  orderStatus?: string; // For order-related records
  trackingNumber?: string; // For shipped orders
  deliveryPartnerId?: string; // For shipped orders
  deliveryPartnerName?: string; // Delivery partner name (stored with order)
  deliveryPartnerWaitingPeriod?: number; // Waiting period (stored with order)
  deliveryDate?: string; // For delivered orders
  createdAt?: string; // Order creation date
  insuranceCarrier?: string; // Insurance carrier name
  waitingPeriod?: number; // Insurance carrier waiting period
}


interface Note {
  _id: string;
  orderId: string;
  text: string;
  addedBy: string;
  addedByRole?: string;
  createdAt: string;
}


interface Doctor {
  employeeId: string;
  name: string;
  specializations: string[];
}

interface FileUploadState {
  selectedFile: File | null;
  isUploading: boolean;
  uploadStatus: string;
  selectedDoctor: string;
  selectedProcedure: string;
  surgeryDate: any;
  enableOnDemand: boolean;
  uploadError: string | null;
}

const OrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  // Read status from navigation state (if coming from dashboard)
  const initialState = (location.state as any)?.status || 'Ready to Order';
  
  const [demographicsData, setDemographicsData] = useState<DemographicsData[]>([]);
  const [filteredData, setFilteredData] = useState<DemographicsData[]>([]);
  const [currentData, setCurrentData] = useState<'demographics' | 'orders'>('demographics');
  const [loading, setLoading] = useState(false);
  const [smartSearchTerm, setSmartSearchTerm] = useState('');
  const [smartSearchField, setSmartSearchField] = useState('all');
  const [totalCount, setTotalCount] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalKey] = useState(0); // Add this to force Upload component reset
  const [isResubmitModalVisible, setIsResubmitModalVisible] = useState(false);
  const [isStatusUpdateModalVisible, setIsStatusUpdateModalVisible] = useState(false);
  const [selectedOrderForStatusUpdate, setSelectedOrderForStatusUpdate] = useState<any>(null);
  const [statusUpdateForm] = Form.useForm();
  const [selectedStatusForUpdate, setSelectedStatusForUpdate] = useState<string>('');
  const [deliveryPartners, setDeliveryPartners] = useState<any[]>([]);
  const [loadingDeliveryPartners, setLoadingDeliveryPartners] = useState(false);
  const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false);
  const [selectedOrderForPayment, setSelectedOrderForPayment] = useState<any>(null);
  const [paymentForm] = Form.useForm();
  const [resubmitRecord, setResubmitRecord] = useState<DemographicsData | null>(null);
  const [resubmitFileState, setResubmitFileState] = useState<FileUploadState>({
    selectedFile: null,
    isUploading: false,
    uploadStatus: '',
    selectedDoctor: '',
    selectedProcedure: '',
    surgeryDate: null,
    enableOnDemand: false,
    uploadError: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [availableProcedures, setAvailableProcedures] = useState<string[]>([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [loadingProcedures, setLoadingProcedures] = useState(false);
  const [statusStats, setStatusStats] = useState({
    'Ready to Order': 0,
    'In Progress': 0,
    'Delivered': 0,
    'Cancelled': 0,
    'Ready to Bill': 0,
    'Payment Due': 0,
    'Payment Denied': 0,
    'Payment Received': 0
  });
  const [selectedStatus, setSelectedStatus] = useState<string | null>(initialState);
  const [showColumnsDropdown, setShowColumnsDropdown] = useState(false);
  const [isNotesModalVisible, setIsNotesModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<DemographicsData | null>(null);
  const [notes, setNotes] = useState<Note[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [viewOrderModalVisible, setViewOrderModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [newNoteText, setNewNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [insuranceFileInfo, setInsuranceFileInfo] = useState<Record<string, any>>({});
  const [invoiceInfo, setInvoiceInfo] = useState<Record<string, any>>({});
  const [isInsuranceUploadModalVisible, setIsInsuranceUploadModalVisible] = useState(false);
  const [selectedRecordForInsurance, setSelectedRecordForInsurance] = useState<DemographicsData | null>(null);
  const [insuranceFileState, setInsuranceFileState] = useState<{
    selectedFile: File | null;
    isUploading: boolean;
    uploadError: string | null;
  }>({
    selectedFile: null,
    isUploading: false,
    uploadError: null
  });
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    orderId: true,
    id: true,
    doctorName: true,
    patientName: true,
    procedureName: true,
    surgeryDate: true,
    filename: user?.role !== 'manufacturer', // Hide filename column for manufacturers
    onDemand: true,
    status: true,
    actions: true
  });
  const [columnOrder, setColumnOrder] = useState<string[]>(
    user?.role === 'manufacturer' 
      ? ['orderId', 'doctorName', 'patientName', 'procedureName', 'surgeryDate', 'status', 'actions']
      : ['orderId', 'doctorName', 'patientName', 'procedureName', 'surgeryDate', 'filename', 'status', 'actions']
  );

  // Table ref for clearing filters and sorting
  const tableRef = useRef<any>(null);

  // Key to force table re-render when clearing filters/sorting
  const [tableKey, setTableKey] = useState(0);


  const [fileState, setFileState] = useState<FileUploadState>({
    selectedFile: null,
    isUploading: false,
    uploadStatus: '',
    selectedDoctor: '',
    selectedProcedure: '',
    surgeryDate: null,
    enableOnDemand: false,
    uploadError: null
  });


  // Fetch notes for a specific order
  const fetchNotes = async (orderId: string) => {
    setNotesLoading(true);
    try {
      const response = await fetch(`/api/notes/${orderId}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setNotes(data.data || []);
      } else {
        message.error('Failed to fetch notes');
        setNotes([]);
      }
    } catch (error) {
      console.error('Error fetching notes:', error);
      message.error('Error fetching notes');
      setNotes([]);
    } finally {
      setNotesLoading(false);
    }
  };

  // Add a new note
  const addNote = async (orderId: string, text: string) => {
    setAddingNote(true);
    try {
      const response = await fetch('/api/notes', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ orderId, text })
      });
      if (response.ok) {
        message.success('Note added successfully');
        setNewNoteText('');
        // Refresh notes list
        await fetchNotes(orderId);
      } else {
        const errorData = await response.json();
        message.error(errorData.message || 'Failed to add note');
      }
    } catch (error) {
      console.error('Error adding note:', error);
      message.error('Error adding note');
    } finally {
      setAddingNote(false);
    }
  };

  // Delete a note
  const deleteNote = async (noteId: string, orderId: string) => {
    try {
      const response = await fetch(`/api/notes/${noteId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        message.success('Note deleted successfully');
        // Refresh notes list using selectedRecord.id (consistent with handleOpenNotesModal)
        if (selectedRecord) {
          await fetchNotes(String(selectedRecord.id));
        } else {
          // Fallback to orderId parameter if selectedRecord is not available
          if (orderId && orderId.trim() !== '') {
            if(orderId.startsWith('MPM-')){
              await fetchNotes(orderId.substring(4));
            } else {
              await fetchNotes(String(orderId));
            }
          } else {
            console.warn('Cannot refresh notes: no valid ID available');
          }
        }
      } else {
        const errorData = await response.json();
        message.error(errorData.message || 'Failed to delete note');
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      message.error('Error deleting note');
    }
  };

  // Fetch all stats from both Ready to Order and Orders APIs
  const fetchAllStats = async (search: string = '', searchField: string = 'all') => {
    try {
      // Fetch Ready to Order count (first page only to get totalCount)
      const readyToOrderParams = new URLSearchParams({
        page: '1',
        limit: '1',
        sortBy: 'uploadedOn',
        sortOrder: 'desc'
      });
      
      if (search) {
        readyToOrderParams.append('search', search);
        readyToOrderParams.append('searchField', searchField);
      }

      // Fetch Orders stats (all statuses)
      const ordersParams = new URLSearchParams({
        page: '1',
        limit: '1'
      });
      
      if (search) {
        ordersParams.append('search', search);
        ordersParams.append('searchField', searchField);
      }

      const readyToOrderUrl = `${BACKEND_URL}/ready-to-order?${readyToOrderParams.toString()}`;
      console.log('🔍 fetchAllStats - Ready to Order URL:', readyToOrderUrl);

      const [readyToOrderResponse, ordersResponse] = await Promise.all([
        fetch(readyToOrderUrl, {
          headers: getAuthHeaders()
        }),
        fetch(`/api/orders?${ordersParams.toString()}`, {
          headers: getAuthHeaders(),
          credentials: 'include'
        })
      ]);

      console.log('🔍 fetchAllStats - Ready to Order Response Status:', readyToOrderResponse.ok, readyToOrderResponse.status);

      let readyToOrderCount = 0;
      let orderStats = {
        'In Progress': 0,
        'Delivered': 0,
        'Cancelled': 0,
        'Ready to Bill': 0,
        'Payment Due': 0,
        'Payment Denied': 0,
        'Payment Received': 0
      };

      if (readyToOrderResponse.ok) {
        const data = await readyToOrderResponse.json();
        console.log('🔍 fetchAllStats - Ready to Order Data:', data);
        readyToOrderCount = data.pagination?.totalCount || 0;
        console.log('🔍 fetchAllStats - Ready to Order Count:', readyToOrderCount);
      } else {
        console.error('🔍 fetchAllStats - Ready to Order Response Error:', readyToOrderResponse.status, readyToOrderResponse.statusText);
      }

      if (ordersResponse.ok) {
        const data = await ordersResponse.json();
        if (data.stats?.statusCounts) {
          orderStats = data.stats.statusCounts;
        }
      }

      // Merge all stats - ensure 'Ready to Order' is set after spread to avoid override
      const newStatusStats = {
        ...orderStats,
        'Ready to Order': readyToOrderCount  // Set after spread to ensure it's not overridden
      };
      
      console.log('🔍 fetchAllStats - Ready to Order Count from API:', readyToOrderCount);
      console.log('🔍 fetchAllStats - Order Stats:', orderStats);
      console.log('🔍 fetchAllStats - Final Status Stats:', newStatusStats);
      setStatusStats(newStatusStats);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Fetch data based on selected status
  const fetchData = async (page: number = currentPage, limit: number = pageSize, search: string = '', searchField: string = 'all', status: string = '') => {
    console.log('📥 fetchData called with status:', status, 'page:', page, 'limit:', limit);
    setLoading(true);
    try {
      // Always fetch all stats to update status boxes
      await fetchAllStats(search, searchField);

      // Special handling for "Ready to Order" status - fetch demographics
      if (status === 'Ready to Order') {
        setCurrentData('demographics');
        await fetchDemographicsData(page, limit, search, searchField, status);
      } else if (status === 'In Progress') {
        // For In Progress, fetch orderIds from Orders collection, then fetch demographics info
        setCurrentData('demographics');
        await fetchDemographicsDataForOrders(page, limit, search, searchField, status);
      } else if (status && ['Delivered', 'Cancelled', 'Ready to Bill', 'Payment Due', 'Payment Denied', 'Payment Received'].includes(status)) {
        // For Delivered, Cancelled, and Payment statuses, fetch orderIds from Orders collection, then fetch demographics info
        setCurrentData('demographics');
        await fetchDemographicsDataForOrders(page, limit, search, searchField, status);
      } else {
        // Default to demographics
        setCurrentData('demographics');
        await fetchDemographicsData(page, limit, search, searchField, status);
      }
    } catch (error) {
      message.error('Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch demographics data with server-side pagination
  const fetchDemographicsData = async (page: number = currentPage, limit: number = pageSize, search: string = '', searchField: string = 'all', status: string = '') => {
    try {
      // Special handling for "Ready to Order" status - use dedicated endpoint
      if (status === 'Ready to Order') {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
          sortBy: 'uploadedOn',
          sortOrder: 'desc'
        });

        if (search) {
          params.append('search', search);
          params.append('searchField', searchField);
        }

        const response = await fetch(`${BACKEND_URL}/ready-to-order?${params.toString()}`, {
          headers: getAuthHeaders()
        });

        if (response.ok) {
          const data = await response.json();
          const readyToOrderRecords = data.data || [];
          const pagination = data.pagination || {};

          // Update pagination info
          setTotalCount(pagination.totalCount || 0);

          // Note: Stats are updated by fetchAllStats, not here

          // Apply client-side sorting for onDemand priority
          const sortedRecords = [...readyToOrderRecords].sort((a: DemographicsData, b: DemographicsData) => {
            if (a.onDemand !== b.onDemand) {
              return b.onDemand ? 1 : -1;
            }
            return new Date(b.uploadedOn).getTime() - new Date(a.uploadedOn).getTime();
          });

          setDemographicsData(sortedRecords);
          setFilteredData(sortedRecords);
        } else {
          message.error('Failed to fetch Ready to Order records');
        }
        return;
      }

      // For other statuses, use regular demographics API
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortBy: 'uploadedOn',
        sortOrder: 'desc'
      });

      if (search) {
        params.append('search', search);
        params.append('searchField', searchField);
      }

      if (status === 'In Progress') {
        // For In Progress, we need to fetch demographics whose orders are in InProgress status
        params.append('status', 'In Progress');
        params.append('hasOrderInProgress', 'true'); // Special flag for backend to filter by order status
      } else if (status) {
        params.append('status', status);
      }

      const response = await fetch(`${BACKEND_URL}?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        const demographics = data.data || [];
        const pagination = data.pagination || {};

        // Update pagination info
        setTotalCount(pagination.totalCount || 0);

        // Note: Stats for non-Ready-to-Order statuses are handled by fetchDemographicsDataForOrders
        // which uses the Orders API

        // Apply client-side sorting for onDemand priority (since server doesn't handle this complex sorting)
        const sortedDemographics = [...demographics].sort((a: DemographicsData, b: DemographicsData) => {
          // First sort by onDemand (true values first)
          if (a.onDemand !== b.onDemand) {
            return b.onDemand ? 1 : -1;
          }
          // Then sort by timestamp (newest first) - this should already be handled by server
          return new Date(b.uploadedOn).getTime() - new Date(a.uploadedOn).getTime();
        });

        setDemographicsData(sortedDemographics);
        setFilteredData(sortedDemographics);
      } else {
        message.error('Failed to fetch demographics data');
      }
    } catch (error) {
      message.error('Error connecting to server');
    }
  };

  // Fetch demographics data for orders (fetch orderIds first, then demographics)
  const fetchDemographicsDataForOrders = async (page: number = currentPage, limit: number = pageSize, search: string = '', searchField: string = 'all', status: string = '') => {
    try {
      // First, fetch orderIds from Orders collection based on status
      const orderParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortBy: 'createdAt',
        sortOrder: 'desc'
      });

      // Add search parameters
      if (search) {
        orderParams.append('search', search);
        orderParams.append('searchField', searchField);
      }

      // Map frontend status to backend order status
      let orderStatus = '';
      if (status === 'In Progress') {
        // Show orders with status YetToBeShipped, ReadyToBeShipped, and Shipped
        orderStatus = 'YetToBeShipped,ReadyToBeShipped,Shipped';
      } else if (status === 'Delivered') {
        // Show orders with status Delivered and Completed
        orderStatus = 'Delivered,Completed';
      } else if (status === 'Cancelled') {
        orderStatus = 'Cancelled';
      } else if (status === 'Ready to Bill') {
        orderStatus = 'ReadyToBill';
      } else if (status === 'Payment Due') {
        orderStatus = 'PaymentDue';
      } else if (status === 'Payment Denied') {
        orderStatus = 'PaymentDenied';
      } else if (status === 'Payment Received') {
        orderStatus = 'PaymentReceived';
      }

      if (orderStatus) {
        orderParams.append('orderStatus', orderStatus);
      }

      const ordersResponse = await fetch(`/api/orders?${orderParams}`, {
        headers: getAuthHeaders(),
      });

      if (ordersResponse.ok) {
        const ordersResult = await ordersResponse.json();
        const orders = ordersResult.data || [];
        console.log('📦 Orders fetched from API:', orders);
        
        // Note: Stats are updated by fetchAllStats, not here
        
        if (orders.length === 0) {
          setDemographicsData([]);
          setFilteredData([]);
          return;
        }

        // Extract demographicsIds from orders
        const demographicsIds = orders.map((order: any) => order.demographicsId).filter(Boolean);
        
        if (demographicsIds.length === 0) {
          setDemographicsData([]);
          setFilteredData([]);
          return;
        }

        // Orders now come with embedded demographics from aggregation
        // No need to fetch demographics separately!
        const demographics = orders.map((order: any) => {
          // Merge order data with embedded demographics data
          const mergedData = {
            // Demographics fields (from embedded demographics object)
            id: order.demographics?.id || order.demographicsId,
            firstName: order.demographics?.firstName || '',
            middleName: order.demographics?.middleName || '',
            lastName: order.demographics?.lastName || '',
            doctorName: order.demographics?.doctorName || 'Unknown',
            procedureName: order.demographics?.procedureName || 'Unknown',
            surgeryDate: order.demographics?.surgeryDate || '',
            onDemand: order.demographics?.onDemand || false,
            // Order fields
            orderId: order.orderId,
            orderStatus: order.orderStatus,
            trackingNumber: order.trackingNumber,
            deliveryPartnerId: order.deliveryPartnerId,
            deliveryPartnerName: order.deliveryPartnerName,
            deliveryPartnerWaitingPeriod: order.deliveryPartnerWaitingPeriod,
            deliveryDate: order.deliveryDate,
            readyToBillDate: order.readyToBillDate,
            lastModifiedDate: order.lastModifiedDate,
            createdAt: order.createdAt,
            paymentInfo: order.paymentInfo,
            updatedAt: order.updatedAt,
            uploadedOn: order.createdAt, // Use order creation date
            filename: order.demographics?.filename || '', // Demographics filename
            contentType: order.demographics?.contentType || '',
            status: 'Accepted', // Orders are from accepted demographics
            isDuplicate: false,
            dateOfBirth: order.demographics?.dateOfBirth || '',
            insuranceCarrier: order.demographics?.insuranceCarrier || '',
            waitingPeriod: order.demographics?.waitingPeriod || null
          };
          console.log('🔗 Merged data for order:', order.orderId, mergedData);
          return mergedData;
        });

        // Backend already filtered via aggregation, no need for client-side filtering
        // Just sort by uploadedOn date
        const sortedDemographics = demographics.sort((a: any, b: any) => 
          new Date(b.uploadedOn).getTime() - new Date(a.uploadedOn).getTime()
        );

        setDemographicsData(sortedDemographics);
        setFilteredData(sortedDemographics);
      } else {
        message.error('Failed to fetch orders data');
      }
    } catch (error) {
      message.error('Error fetching demographics data for orders');
    }
  };



  // Note: Status stats are now fetched via fetchAllStats which fetches both Ready to Order and Orders stats
  // This ensures stats always match the filtered/searched data


  // Handle status filter click
  const handleStatusFilterClick = (status: string) => {
    setSelectedStatus(status);
    setCurrentPage(1); // Reset to first page
  };

  // Fetch doctors from database
  const fetchDoctors = async () => {
    setLoadingDoctors(true);
    try {
      const response = await fetch(`${BACKEND_URL}/doctors`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setDoctors(data.data || []);
      } else {
        message.error('Failed to fetch doctors');
      }
    } catch (error) {
      message.error('Error fetching doctors');
    } finally {
      setLoadingDoctors(false);
    }
  };

  // Fetch procedures by doctor
  const fetchProceduresByDoctor = async (doctorId: string) => {
    setLoadingProcedures(true);
    try {
      const response = await fetch(`${BACKEND_URL}/doctors/${doctorId}/procedures`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableProcedures(data.data || []);
      } else {
        message.error('Failed to fetch procedures');
        setAvailableProcedures([]);
      }
    } catch (error) {
      message.error('Error fetching procedures');
      setAvailableProcedures([]);
    } finally {
      setLoadingProcedures(false);
    }
  };

  // Fetch delivery partners
  const fetchDeliveryPartners = async () => {
    setLoadingDeliveryPartners(true);
    try {
      const response = await fetch('/api/configuration/delivery-partners?activeOnly=true', {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setDeliveryPartners(data.data || []);
      } else {
        message.error('Failed to fetch delivery partners');
        setDeliveryPartners([]);
      }
    } catch (error) {
      message.error('Error fetching delivery partners');
      setDeliveryPartners([]);
    } finally {
      setLoadingDeliveryPartners(false);
    }
  };

  // Calculate days pending for delivered orders
  const calculateDaysPending = (record: DemographicsData): number | null => {
    if (!record.deliveryDate) return null;
    
    // Get insurance carrier waiting period (simplified to 2 priorities only)
    let waitingPeriod = 30; // Default to 30 days
    if (record.waitingPeriod !== undefined && record.waitingPeriod !== null) {
      // Use insurance carrier waiting period (preferred)
      waitingPeriod = record.waitingPeriod;
    }
    // Otherwise, default to 30 days (no delivery partner fallback)

    // Calculate days since delivery
    const deliveryDateObj = new Date(record.deliveryDate);
    const currentDate = new Date();
    const daysSinceDelivery = Math.floor((currentDate.getTime() - deliveryDateObj.getTime()) / (1000 * 60 * 60 * 24));

    // Days pending = waitingPeriod - daysSinceDelivery
    const daysPending = waitingPeriod - daysSinceDelivery;

    return daysPending;
  };

  // Calculate days since delivered for Ready to Bill orders
  const calculateDaysSinceDelivered = (record: DemographicsData): number | null => {
    if (!record.deliveryDate) return null;
    
    const deliveryDateObj = new Date(record.deliveryDate);
    const currentDate = new Date();
    const daysSinceDelivery = Math.floor((currentDate.getTime() - deliveryDateObj.getTime()) / (1000 * 60 * 60 * 24));
    
    return daysSinceDelivery;
  };

  // Fetch status stats first on component mount (before data)
  useEffect(() => {
    fetchAllStats(); // Fetch all stats (Ready to Order + Orders)
  }, []);

  useEffect(() => {
    fetchDoctors(); // Fetch doctors on component mount
  }, []);

  useEffect(() => {
    fetchDeliveryPartners(); // Fetch delivery partners on component mount
  }, []);

  // Ensure initial data loads on component mount
  useEffect(() => {
    // Load initial data for the default selected status
    console.log('🚀 Initial load - selectedStatus:', selectedStatus, 'pageSize:', pageSize);
    if (selectedStatus) {
      fetchData(1, pageSize, '', 'all', selectedStatus);
    }
  }, []); // Empty dependency array to run only on mount

  // Fetch insurance info for In Progress, Accepted, and Rejected records
  useEffect(() => {
    const recordsWithInsurance = demographicsData.filter(record => 
      record.status === 'In Progress' || record.status === 'Accepted' || record.status === 'Rejected'
    );
    recordsWithInsurance.forEach(record => {
      fetchInsuranceInfo(record.id);
    });
  }, [demographicsData]);

  // Fetch invoice info for Payment Received orders
  useEffect(() => {
    const paymentReceivedOrders = demographicsData.filter(record => 
      record.orderStatus === 'PaymentReceived'
    );
    paymentReceivedOrders.forEach(record => {
      if (record.orderId) {
        fetchInvoiceInfo(record.orderId);
      }
    });
  }, [demographicsData]);

  // Handle click outside to close columns dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if click is outside the columns dropdown and button
      if (showColumnsDropdown && 
          !target.closest('[data-columns-dropdown]') && 
          !target.closest('[data-columns-button]')) {
        setShowColumnsDropdown(false);
      }
    };

    if (showColumnsDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnsDropdown]);

  // Handle smart search
  const handleSmartSearch = (searchTerm: string, selectedField: string = 'all') => {
    // console.log('🔄 handleSmartSearch: called with searchTerm:', searchTerm, 'selectedField:', selectedField, 'current smartSearchTerm:', smartSearchTerm);
    
    // Only reset to first page if the search term has actually changed
    const hasSearchTermChanged = searchTerm !== smartSearchTerm;
    const isNewSearch = searchTerm.trim() !== '' && hasSearchTermChanged;
    
    setSmartSearchTerm(searchTerm);
    setSmartSearchField(selectedField);
    
    if (isNewSearch) {
      // console.log('🔄 handleSmartSearch: setCurrentPage(1) called because searchTerm changed to:', searchTerm);
      setCurrentPage(1); // Reset to first page when searching
    } else {
      // console.log('🔄 handleSmartSearch: NOT resetting page because searchTerm unchanged or empty');
    }
  };

  // Update filteredData when demographicsData changes (for server-side pagination)
  useEffect(() => {
    setFilteredData(demographicsData);
  }, [demographicsData]);

  // Fetch paginated data when filters or pagination change
  useEffect(() => {
    fetchData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || '');
  }, [currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus]);

  // Note: Stats are now fetched via fetchAllStats which is called in fetchData
  // This ensures stats always reflect the current search/filter

  // Handle PDF viewing
  const handleViewPDF = (id: string) => {
    const viewerUrl = `/view-pdf/demographics/${id}`;
    window.open(viewerUrl, '_blank');
  };








  // Handle clear table filters and sorting only
  const handleClearTableFilters = () => {
    // Force table re-render to clear all filters and sorting state completely
    setTableKey(prev => prev + 1);

    message.success('Table filters and sorting cleared');
  };

  // Handle column visibility change
  const handleColumnVisibilityChange = (columnKey: string, checked: boolean) => {
    setColumnVisibility(prev => ({
      ...prev,
      [columnKey]: checked
    }));
  };

  // Column configuration with display names
  const columnConfigMap = {
    orderId: 'Order ID',
    doctorName: 'Doctor Name',
    patientName: 'Patient Name',
    procedureName: 'Procedure',
    surgeryDate: 'Surgery Date',
    filename: 'Demographics File',
    status: 'Status',
    actions: 'Actions'
  };

  // Get ordered column config based on columnOrder
  const getOrderedColumnConfig = () => {
    return columnOrder
      .filter(key => user?.role === 'manufacturer' ? key !== 'filename' : true) // Filter out filename for manufacturers
      .map(key => ({
        key,
        label: columnConfigMap[key as keyof typeof columnConfigMap]
      }));
  };

  // Handle drag end for column reordering
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setColumnOrder((items) => {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over.id as string);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // Sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Filter and order columns based on visibility state and column order
  const getVisibleColumns = (originalColumns: ColumnsType<DemographicsData>) => {
    // First filter by visibility
    const visibleColumns = originalColumns.filter(column => {
      const columnKey = column.key as string;
      return columnVisibility[columnKey] !== false;
    });

    // Then sort by column order
    return visibleColumns.sort((a, b) => {
      const indexA = columnOrder.indexOf(a.key as string);
      const indexB = columnOrder.indexOf(b.key as string);
      return indexA - indexB;
    });
  };


  // Handle start verification
  const handleStartVerification = async (id: string) => {
    try {
      const response = await fetch(`${BACKEND_URL}/${id}/start-verification`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        message.success('Verification started successfully');
        fetchData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || ''); // Refresh data (includes stats)
      } else {
        const errorData = await response.json();
        message.error(errorData.message || 'Failed to start verification');
      }
    } catch (error) {
      message.error('Error starting verification');
    }
  };

  // Handle create order - navigate to SubmitOrderPage instead of creating order immediately
  const handleCreateOrder = async (demographicsId: string, record?: DemographicsData) => {
    console.log('🚀 handleCreateOrder called with demographicsId:', demographicsId);
    try {
      // Navigate to submit order page without creating an order first
      const recordData = record ? {
        ...record,
        demographicsId: demographicsId
      } : {
        id: demographicsId,
        demographicsId: demographicsId,
        firstName: 'Unknown',
        lastName: 'Patient',
        doctorName: 'Unknown Doctor',
        procedureName: 'Unknown Procedure'
      };
      
      console.log('About to navigate to:', `/submitOrder/demographics/${demographicsId}`);
      console.log('Record data:', recordData);
      
      try {
        navigate(`/submitOrder/demographics/${demographicsId}`, {
          state: { recordData: recordData }
        });
        console.log('Navigation to SubmitOrderPage called successfully');
      } catch (navError) {
        console.error('Navigate function failed, trying window.location:', navError);
        // Fallback to window.location
        window.location.href = `/submitOrder/demographics/${demographicsId}`;
      }
      
    } catch (error) {
      console.error('Error navigating to submit order page:', error);
      message.error('Error navigating to order creation page');
    }
  };

  // Handle view order
  const handleViewOrder = async (orderId: string) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/orders/${orderId}`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const result = await response.json();
        const orderData = result.data;
        
        // Set the order data for the modal
        setSelectedOrder(orderData);
        setViewOrderModalVisible(true);
      } else {
        message.error('Failed to fetch order details');
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
      message.error('Error fetching order details');
    } finally {
      setLoading(false);
    }
  };

  // Handle order form
  const handleOrderForm = async (orderId: string) => {
    try {
      const response = await fetch(`/api/orders/${orderId}/order-form`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        // If it's a PDF, open in viewer
        if (response.headers.get('content-type') === 'application/pdf') {
          const viewerUrl = `/view-pdf/order/${orderId}`;
          window.open(viewerUrl, '_blank');
        } else {
          // Handle status responses
          const data = await response.json();
          if (data.data?.orderFormStatus === 'started') {
            message.info('Order form generation has started. Please check again in a few moments.');
          } else if (data.data?.orderFormStatus === 'inprogress') {
            message.info('Order form is being generated. Please wait and try again.');
          } else if (data.data?.orderFormStatus === 'failed') {
            message.error('Order form generation failed. Please contact support.');
          } else {
            message.info(data.message || 'Order form is not ready yet.');
          }
        }
      } else {
        const errorData = await response.json();
        message.error(errorData.message || 'Failed to get order form');
      }
    } catch (error) {
      console.error('Error getting order form:', error);
      message.error('Error getting order form');
    }
  };

  // Handle edit order
  const handleEditOrder = (orderId: string, orderStatus?: string) => {
    try {
      // Check if user is practitioner and restrict editing based on order status
      if (user?.role === 'practitioner') {
        const allowedStatuses = ['YetToBeShipped', 'ReadyToBeShipped'];
        if (orderStatus && !allowedStatuses.includes(orderStatus)) {
          message.error(`You can only edit orders with status 'YetToBeShipped' or 'ReadyToBeShipped'. Current status: ${orderStatus}`);
          return;
        }
      }
      
      // Navigate to SubmitOrderPage with edit mode
      navigate(`/orders/submitOrder/${orderId}?mode=edit`);
    } catch (error) {
      console.error('Error navigating to edit order page:', error);
      message.error('Error navigating to edit order page');
    }
  };

  // Handle reorder (delete cancelled order and move demographics back to Ready to Order)
  const handleReorderOrder = (orderId: string, demographicsId: string) => {
    Modal.confirm({
      title: 'Reorder',
      content: (
        <div>
          <p><strong>Are you sure you want to reorder this cancelled order?</strong></p>
          <p>This action will:</p>
          <ul>
            <li>Delete the cancelled order from the system</li>
            <li>Save the order data to deletedOrders collection for tracing</li>
            <li>Move the demographics record back to Ready to Order table</li>
            <li>Allow you to create a new order from this record</li>
          </ul>
          <p style={{ color: '#52c41a', fontWeight: 'bold' }}>You can then click "Create Order" to start a new order process.</p>
        </div>
      ),
      okText: 'Yes, Reorder',
      cancelText: 'No, Keep Cancelled',
      okType: 'primary',
      width: 500,
      onOk: async () => {
        try {
          console.log('Reordering order:', orderId, 'demographicsId:', demographicsId);
          
          // Call the reorder endpoint which deletes the order and saves it to deletedOrders
          const response = await fetch(`/api/orders/${orderId}/reorder`, {
            method: 'PUT',
            headers: {
              ...getAuthHeaders(),
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            message.success('Order reordered successfully. The demographics record is now available in Ready to Order.');
            // Refresh the data and stats
            fetchData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || '');
          } else {
            const errorData = await response.json();
            message.error(errorData.message || 'Failed to reorder');
          }
        } catch (error) {
          console.error('Error reordering:', error);
          message.error('Error reordering');
        }
      }
    });
  };

  // Handle status update modal
  const handleUpdateOrderStatus = (order: any) => {
    setSelectedOrderForStatusUpdate(order);
    setSelectedStatusForUpdate('');
    setIsStatusUpdateModalVisible(true);
    statusUpdateForm.resetFields();
  };

  // Handle status update form submission
  const handleStatusUpdateSubmit = async (values: any) => {
    try {
      const { newStatus, trackingId, deliveryPartnerId, deliveryDate, printingSlipBase64, printingSlipFilename, printingSlipContentType } = values;
      
      console.log('🚀 Form submission values:', {
        newStatus,
        trackingId,
        deliveryDate,
        printingSlipBase64: printingSlipBase64 ? 'present' : 'missing',
        printingSlipFilename,
        printingSlipContentType
      });
      
      // Prepare request body based on status
      const requestBody: any = {
        orderStatus: newStatus,
        modifiedBy: user?.email || 'manufacturer',
        manufacturerInfo: {
          userId: user?.id || 'unknown',
          email: user?.email || 'unknown',
          role: user?.role || 'unknown',
          name: user?.name || 'Unknown User',
          fullName: user?.name || 'Unknown User'
        }
      };

      // Add status-specific fields
      if (newStatus === 'Shipped') {
        if (trackingId) {
          requestBody.trackingId = trackingId;
        }
        if (deliveryPartnerId) {
          requestBody.deliveryPartnerId = deliveryPartnerId;
        }
      } else if (newStatus === 'Delivered') {
        if (deliveryDate) {
          requestBody.deliveryDate = deliveryDate.format('YYYY-MM-DD');
        }
        if (printingSlipBase64) {
          requestBody.printingSlip = printingSlipBase64;
          requestBody.printingSlipFilename = printingSlipFilename;
          requestBody.printingSlipContentType = printingSlipContentType;
        }
      }
      
      const response = await fetch(`/api/orders/${selectedOrderForStatusUpdate.orderId || selectedOrderForStatusUpdate.id}/status`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        message.success(`Order status updated to ${newStatus} successfully`);
        setIsStatusUpdateModalVisible(false);
        setSelectedOrderForStatusUpdate(null);
        setSelectedStatusForUpdate('');
        statusUpdateForm.resetFields();
        // Clear additional fields
        statusUpdateForm.setFieldsValue({
          printingSlipBase64: null,
          printingSlipFilename: null,
          printingSlipContentType: null
        });
        // Refresh the data and stats
        fetchData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || '');
      } else {
        const errorData = await response.json();
        message.error(errorData.message || 'Failed to update order status');
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      message.error('Error updating order status');
    }
  };

  // Handle tracking number click
  const handleTrackingNumberClick = async (trackingNumber: string, deliveryPartnerId?: string) => {
    // If no delivery partner ID provided, use default UPS URL
    if (!deliveryPartnerId) {
      const upsUrl = `https://www.ups.com/track?loc=en_US&requester=ST/&tracknum=${trackingNumber}`;
      window.open(upsUrl, '_blank');
      return;
    }

    // Try to find delivery partner in loaded state first (faster)
    const partner = deliveryPartners.find(p => p._id === deliveryPartnerId);
    if (partner) {
      const trackingUrl = `${partner.url}${trackingNumber}`;
      window.open(trackingUrl, '_blank');
      return;
    }

    // Fallback: Fetch delivery partner details from API if not in state
    try {
      const response = await fetch(`/api/configuration/delivery-partners/${deliveryPartnerId}`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        const deliveryPartner = data.data;
        const trackingUrl = `${deliveryPartner.url}${trackingNumber}`;
        window.open(trackingUrl, '_blank');
      } else {
        // Fallback to UPS if delivery partner not found
        message.warning('Delivery partner not found, using default UPS tracking');
        const upsUrl = `https://www.ups.com/track?loc=en_US&requester=ST/&tracknum=${trackingNumber}`;
        window.open(upsUrl, '_blank');
      }
    } catch (error) {
      console.error('Error fetching delivery partner:', error);
      // Fallback to UPS on error
      const upsUrl = `https://www.ups.com/track?loc=en_US&requester=ST/&tracknum=${trackingNumber}`;
      window.open(upsUrl, '_blank');
    }
  };

  // Handle cancel order
  const handleCancelOrder = (orderId: string) => {
    Modal.confirm({
      title: 'Cancel Order',
      content: (
        <div>
          <p><strong>Are you sure you want to cancel this order?</strong></p>
          <p>This action will:</p>
          <ul>
            <li>Change the order status to 'Cancelled'</li>
            <li>Move the order to the Cancelled orders table</li>
            <li>Prevent further editing of this order</li>
          </ul>
          <p style={{ color: '#ff4d4f', fontWeight: 'bold' }}>This action cannot be undone.</p>
        </div>
      ),
      okText: 'Yes, Cancel Order',
      cancelText: 'No, Keep Order',
      okType: 'danger',
      width: 500,
      onOk: async () => {
        try {
          console.log('Cancelling order:', orderId);
          
          // Update order status to 'Cancelled' instead of deleting
          const response = await fetch(`/api/orders/${orderId}/status`, {
            method: 'PUT',
            headers: {
              ...getAuthHeaders(),
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              orderStatus: 'Cancelled'
            })
          });

          if (response.ok) {
            message.success('Order cancelled successfully. It has been moved to the Cancelled orders table.');
            // Refresh the data and stats
            fetchData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || '');
          } else {
            const errorData = await response.json();
            message.error(errorData.message || 'Failed to cancel order');
          }
        } catch (error) {
          console.error('Error cancelling order:', error);
          message.error('Error cancelling order');
        }
      }
    });
  };

  // Handle cancel Ready To Order - creates a new cancelled order record
  const handleCancelReadyToOrder = (demographicsId: string) => {
    Modal.confirm({
      title: 'Cancel Ready To Order',
      content: (
        <div>
          <p><strong>Are you sure you want to cancel this Ready To Order record?</strong></p>
          <p>This action will:</p>
          <ul>
            <li>Create a new cancelled order record</li>
            <li>Move the record to the Cancelled orders table</li>
            <li>Prevent further editing of this record</li>
          </ul>
          <p style={{ color: '#ff4d4f', fontWeight: 'bold' }}>This action cannot be undone.</p>
        </div>
      ),
      okText: 'Yes, Cancel Order',
      cancelText: 'No, Keep Order',
      okType: 'danger',
      width: 500,
      onOk: async () => {
        try {
          console.log('Cancelling Ready To Order record:', demographicsId);
          
          // Create a new cancelled order record instead of updating existing
          const response = await fetch('/api/orders/cancel-ready-to-order', {
            method: 'POST',
            headers: {
              ...getAuthHeaders(),
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              demographicsId: demographicsId,
              orderStatus: 'Cancelled'
            })
          });

          if (response.ok) {
            message.success('Ready To Order record cancelled successfully. A new cancelled order has been created.');
            // Refresh the data and stats
            fetchData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || '');
          } else {
            const errorData = await response.json();
            message.error(errorData.message || 'Failed to cancel Ready To Order record');
          }
        } catch (error) {
          console.error('Error cancelling Ready To Order record:', error);
          message.error('Error cancelling Ready To Order record');
        }
      }
    });
  };


  // Handle doc package view/download
  const handleDocPackage = async (orderId: string) => {
    try {
      // First check the status
      const statusResponse = await fetch(`/api/orders/${orderId}/doc-package/status`, {
        headers: getAuthHeaders()
      });

      if (!statusResponse.ok) {
        message.error('Failed to check doc package status');
        return;
      }

      const statusData = await statusResponse.json();
      
      if (statusData.data.status === 'pending' || statusData.data.status === 'inprogress') {
        message.info('Please wait while the Doc Package is being created...');
        return;
      }

      if (statusData.data.status === 'failed') {
        message.error('Doc package creation failed. Please contact support.');
        return;
      }

      if (statusData.data.status === 'created') {
        // Download the doc package
        const downloadResponse = await fetch(`/api/orders/${orderId}/doc-package/download`, {
          headers: getAuthHeaders()
        });

        if (downloadResponse.ok) {
          const blob = await downloadResponse.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = statusData.data.filename || `doc-package-${orderId}.pdf`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        } else {
          const errorData = await downloadResponse.json();
          message.error(errorData.message || 'Failed to download doc package');
        }
      }
    } catch (error) {
      console.error('Error handling doc package:', error);
      message.error('Error handling doc package');
    }
  };

  // Handle upload claim form
  const handleUploadClaimForm = (orderId: string) => {
    // Create a file input element
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/pdf';
    
    input.onchange = async (e: Event) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];
      
      if (!file) {
        return;
      }

      // Check if file is PDF
      if (file.type !== 'application/pdf') {
        message.error('Please select a PDF file');
        return;
      }

      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        message.error('File size must be less than 10MB');
        return;
      }

      try {
        const formData = new FormData();
        formData.append('claimForm', file);

        const response = await fetch(`/api/orders/${orderId}/claim-form/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`
          },
          body: formData
        });

        if (response.ok) {
          message.success('Claim form uploaded successfully');
        } else {
          const errorData = await response.json();
          message.error(errorData.message || 'Failed to upload claim form');
        }
      } catch (error) {
        console.error('Error uploading claim form:', error);
        message.error('Error uploading claim form');
      }
    };
    
    input.click();
  };

  // Handle view claim form
  const handleViewClaimForm = async (orderId: string) => {
    try {
      const response = await fetch(`/api/orders/${orderId}/claim-form/download`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        
        // Open in new tab instead of downloading
        window.open(url, '_blank');
        
        // Clean up after a delay
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 100);
      } else {
        const errorData = await response.json();
        message.error(errorData.message || 'Failed to view claim form');
      }
    } catch (error) {
      console.error('Error viewing claim form:', error);
      message.error('Error viewing claim form');
    }
  };

  // Handle mark as billed - move from Ready to Bill to Payment Due
  const handleMarkAsBilled = async (orderId: string) => {
    Modal.confirm({
      title: 'Mark as Billed',
      content: 'Are you sure you want to mark this order as billed? This will move it to Payment Due status.',
      okText: 'Yes, Mark as Billed',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const response = await fetch(`/api/orders/${orderId}/status`, {
            method: 'PUT',
            headers: {
              ...getAuthHeaders(),
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              orderStatus: 'PaymentDue',
              modifiedBy: user?.email || user?.name || 'User'
            })
          });

          if (response.ok) {
            message.success('Order marked as billed and moved to Payment Due');
            // Refresh the data and stats
            fetchData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || '');
          } else {
            const errorData = await response.json();
            message.error(errorData.message || 'Failed to mark order as billed');
          }
        } catch (error) {
          console.error('Error marking order as billed:', error);
          message.error('Error marking order as billed');
        }
      }
    });
  };

  // Handle add payment - show modal for Payment Due records
  const handleAddPayment = (record: any) => {
    setSelectedOrderForPayment(record);
    setIsPaymentModalVisible(true);
    
    // Pre-populate form with existing payment data if available
    if (record.paymentInfo) {
      paymentForm.setFieldsValue({
        primaryAmount: record.paymentInfo.primaryAmount || 0,
        secondaryAmount: record.paymentInfo.secondaryAmount || 0,
        patientAmount: record.paymentInfo.patientAmount || 0
      });
    } else {
      // Set default values to 0 if no payment info exists
      paymentForm.setFieldsValue({
        primaryAmount: 0,
        secondaryAmount: 0,
        patientAmount: 0
      });
    }
  };

  // Handle payment form submission
  const handlePaymentSubmit = async (values: any) => {
    try {
      const { primaryAmount, secondaryAmount, patientAmount } = values;
      
      const response = await fetch(`/api/orders/${selectedOrderForPayment.orderId || selectedOrderForPayment.id}/payment`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          primaryAmount: Number(primaryAmount) || 0,
          secondaryAmount: Number(secondaryAmount) || 0,
          patientAmount: Number(patientAmount) || 0
        })
      });

      if (response.ok) {
        message.success('Payment information added successfully');
        setIsPaymentModalVisible(false);
        setSelectedOrderForPayment(null);
        paymentForm.resetFields();
        // Refresh the data
        fetchData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || '');
      } else {
        const errorData = await response.json();
        message.error(errorData.message || 'Failed to add payment information');
      }
    } catch (error) {
      console.error('Error adding payment information:', error);
      message.error('Error adding payment information');
    }
  };

  // Handle upload EOB form
  const handleUploadEobForm = (orderId: string) => {
    // Create a file input element
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/pdf';
    
    input.onchange = async (e: Event) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];
      
      if (!file) {
        return;
      }

      // Check if file is PDF
      if (file.type !== 'application/pdf') {
        message.error('Please select a PDF file');
        return;
      }

      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        message.error('File size must be less than 10MB');
        return;
      }

      try {
        const formData = new FormData();
        formData.append('eobForm', file);

        const response = await fetch(`/api/orders/${orderId}/eob-form/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`
          },
          body: formData
        });

        if (response.ok) {
          message.success('EOB form uploaded successfully');
        } else {
          const errorData = await response.json();
          message.error(errorData.message || 'Failed to upload EOB form');
        }
      } catch (error) {
        console.error('Error uploading EOB form:', error);
        message.error('Error uploading EOB form');
      }
    };
    
    input.click();
  };

  // Handle view EOB form
  const handleViewEobForm = async (orderId: string) => {
    try {
      const response = await fetch(`/api/orders/${orderId}/eob-form/download`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        
        // Open in new tab instead of downloading
        window.open(url, '_blank');
        
        // Clean up after a delay
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 100);
      } else {
        const errorData = await response.json();
        message.error(errorData.message || 'Failed to view EOB form');
      }
    } catch (error) {
      console.error('Error viewing EOB form:', error);
      message.error('Error viewing EOB form');
    }
  };

  // Fetch insurance file info for a record
  const fetchInsuranceInfo = async (demographicsId: string) => {
    try {
      const response = await fetch(`/api/insurance/verifications/${demographicsId}/info`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setInsuranceFileInfo(prev => ({
          ...prev,
          [demographicsId]: data
        }));
      }
    } catch (error) {
      console.error('Error fetching insurance info:', error);
    }
  };

  // Fetch invoice info for an order
  const fetchInvoiceInfo = async (orderId: string) => {
    try {
      const response = await fetch(`/api/invoices/order/${orderId}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const invoice = await response.json();
        setInvoiceInfo(prev => ({
          ...prev,
          [orderId]: invoice
        }));
      } else {
        // If no invoice found, set as null
        setInvoiceInfo(prev => ({
          ...prev,
          [orderId]: null
        }));
      }
    } catch (error) {
      console.error('Error fetching invoice info:', error);
      setInvoiceInfo(prev => ({
        ...prev,
        [orderId]: null
      }));
    }
  };

  // Handle insurance file upload
  const handleInsuranceFileUpload = async () => {
    if (!selectedRecordForInsurance || !insuranceFileState.selectedFile) {
      message.error('Please select a file to upload');
      return;
    }

    setInsuranceFileState(prev => ({ ...prev, isUploading: true, uploadError: null }));

    const formData = new FormData();
    formData.append('insuranceFile', insuranceFileState.selectedFile);

    try {
      const response = await fetch(`/api/insurance/verifications/${selectedRecordForInsurance.id}/upload`, {
        method: 'POST',
        headers: getAuthHeadersForUpload(),
        body: formData,
      });

      if (response.ok) {
        message.success('Insurance file uploaded successfully');
        setIsInsuranceUploadModalVisible(false);
        setInsuranceFileState({
          selectedFile: null,
          isUploading: false,
          uploadError: null
        });
        setSelectedRecordForInsurance(null);
        // Refresh insurance info for this record
        await fetchInsuranceInfo(selectedRecordForInsurance.id);
      } else {
        const errorData = await response.json();
        message.error(errorData.message || 'Failed to upload insurance file');
        setInsuranceFileState(prev => ({ ...prev, uploadError: errorData.message }));
      }
    } catch (error) {
      message.error('Error uploading insurance file');
      setInsuranceFileState(prev => ({ ...prev, uploadError: 'Network error' }));
    } finally {
      setInsuranceFileState(prev => ({ ...prev, isUploading: false }));
    }
  };

  // Handle accept insurance
  const handleAcceptInsurance = async (id: string) => {
    try {
      const response = await fetch(`/api/insurance/verifications/${id}/accept`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          acceptedBy: 'Insurance Verifier' // You can get this from user context
        })
      });

      if (response.ok) {
        message.success('Insurance verification accepted successfully');
        fetchData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || ''); // Refresh data (includes stats)
      } else {
        const errorData = await response.json();
        message.error(errorData.message || 'Failed to accept insurance verification');
      }
    } catch (error) {
      message.error('Error accepting insurance verification');
    }
  };

  // Handle reject insurance
  const handleRejectInsurance = async (id: string) => {
    Modal.confirm({
      title: 'Reject Insurance Verification',
      content: (
        <div>
          <p>Are you sure you want to reject this insurance verification?</p>
          <Input.TextArea
            placeholder="Please provide a reason for rejection..."
            id="rejection-reason"
            rows={3}
          />
        </div>
      ),
      okText: 'Reject',
      cancelText: 'Cancel',
      onOk: async () => {
        const reason = (document.getElementById('rejection-reason') as HTMLTextAreaElement)?.value || '';
        
        try {
          const response = await fetch(`/api/insurance/verifications/${id}/reject`, {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify({
              rejectedBy: 'Insurance Verifier', // You can get this from user context
              rejectedReason: reason
            })
          });

          if (response.ok) {
            message.success('Insurance verification rejected successfully');
            fetchData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || ''); // Refresh data (includes stats)
          } else {
            const errorData = await response.json();
            message.error(errorData.message || 'Failed to reject insurance verification');
          }
        } catch (error) {
          message.error('Error rejecting insurance verification');
        }
      }
    });
  };

  // Handle view insurance file
  const handleViewInsuranceFile = (id: string) => {
    const viewerUrl = `/view-pdf/insurance/${id}`;
    window.open(viewerUrl, '_blank');
  };

  // Table columns configuration for regular demographics
  const columns: ColumnsType<DemographicsData> = [
    {
      title: 'Order ID',
      key: 'orderId',
      width: 150,
      render: (_, record) => {
        let displayOrderId = 'N/A';
        
        // Display Order ID with MPM prefix only for Ready to Order table
        if (record.orderId) {
          // For tables other than Ready to Order, show orderId without MPM prefix
          if (selectedStatus !== 'Ready to Order') {
            displayOrderId = record.orderId;
          } else {
            // For Ready to Order table, show with MPM prefix
            displayOrderId = `MPM-${record.orderId}`;
          }
        } else if (record.id) {
          // For Ready to Order records that don't have orders yet
          displayOrderId = `MPM-${record.id}`;
        }
        
        // Display Order ID as plain text
        return (
          <span
            style={{
              fontWeight: 600,
              fontFamily: "'Courier New', monospace",
              fontSize: '0.9375rem'
            }}
          >
            {displayOrderId}
          </span>
        );
      },
      sorter: (a, b) => {
        const aId = a.orderId || a.id || '';
        const bId = b.orderId || b.id || '';
        return aId.localeCompare(bId);
      },
    },
    {
      title: 'Doctor Name',
      dataIndex: 'doctorName',
      key: 'doctorName',
      width: 200,
      sorter: (a, b) => a.doctorName.localeCompare(b.doctorName),
      filters: Array.from(new Set(demographicsData.map(item => item.doctorName))).map(name => ({
        text: name,
        value: name,
      })),
      onFilter: (value, record) => record.doctorName === value,
    },
    {
      title: 'Patient Name',
      key: 'patientName',
      width: 200,
      render: (_, record) => {
        const fullName = [record.firstName, record.middleName, record.lastName]
          .filter(name => name && name.trim())
          .join(' ');
        return fullName || 'No name provided';
      },
      sorter: (a, b) => {
        const nameA = [a.firstName, a.middleName, a.lastName].filter(name => name && name.trim()).join(' ');
        const nameB = [b.firstName, b.middleName, b.lastName].filter(name => name && name.trim()).join(' ');
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: 'Procedure',
      dataIndex: 'procedureName',
      key: 'procedureName',
      width: 180,
      sorter: (a, b) => a.procedureName.localeCompare(b.procedureName),
      filters: Array.from(new Set(demographicsData.map(item => item.procedureName))).map(name => ({
        text: name,
        value: name,
      })),
      onFilter: (value, record) => record.procedureName === value,
    },
     {
              title: 'Surgery Date',
        dataIndex: 'surgeryDate',
        key: 'surgeryDate',
        width: 130,
        render: (date) => {
          // Surgery Date is always read-only (view-only) in Insurance Page
          return (
            <span style={{ color: '#666', fontStyle: 'italic' }}>
              {date ? new Date(date).toLocaleDateString() : 'Not set'}
            </span>
          );
        },
        sorter: (a, b) => new Date(a.surgeryDate).getTime() - new Date(b.surgeryDate).getTime(),
     },
    // Delivery Date - For Delivered and Ready to Bill statuses
    ...(['Delivered', 'Ready to Bill'].includes(selectedStatus || '') ? [{
      title: 'Delivery Date',
      dataIndex: 'deliveryDate',
      key: 'deliveryDate',
      width: 130,
      render: (date: string) => {
        return (
          <span style={{ color: '#52c41a', fontWeight: '500' }}>
            {date ? new Date(date).toLocaleDateString() : 'Not set'}
          </span>
        );
      },
      sorter: (a: any, b: any) => {
        if (!a.deliveryDate) return 1;
        if (!b.deliveryDate) return -1;
        return new Date(a.deliveryDate).getTime() - new Date(b.deliveryDate).getTime();
      },
    }] : []),
    // Insurance Carrier - For Delivered, Ready to Bill, Payment Due, Payment Received, Payment Denied statuses
    ...(['Delivered', 'Ready to Bill', 'Payment Due', 'Payment Received', 'Payment Denied'].includes(selectedStatus || '') ? [{
      title: 'Insurance Carrier',
      dataIndex: 'insuranceCarrier',
      key: 'insuranceCarrier',
      width: 150,
      render: (insuranceCarrier: string, record: DemographicsData) => {
        if (!insuranceCarrier || insuranceCarrier.trim() === '') {
          return <span style={{ color: '#999', fontStyle: 'italic' }}>Not set</span>;
        }
        return (
          <span style={{ color: '#1890ff', fontWeight: '500' }}>
            {insuranceCarrier}
          </span>
        );
      },
      sorter: (a: any, b: any) => {
        const aCarrier = a.insuranceCarrier || '';
        const bCarrier = b.insuranceCarrier || '';
        return aCarrier.localeCompare(bCarrier);
      },
      filters: Array.from(new Set(demographicsData
        .filter(item => item.insuranceCarrier && item.insuranceCarrier.trim() !== '')
        .map(item => item.insuranceCarrier)
      )).filter((carrier): carrier is string => carrier !== undefined && carrier !== null)
        .map(carrier => ({
          text: carrier,
          value: carrier,
        })),
      onFilter: (value: any, record: any) => record.insuranceCarrier === value,
    }] : []),
    // Days Pending - Only for Delivered status
    ...(selectedStatus === 'Delivered' ? [{
      title: 'Days Pending',
      key: 'daysPending',
      width: 120,
      render: (_: any, record: DemographicsData) => {
        const daysPending = calculateDaysPending(record);
        
        if (daysPending === null) {
          return <span style={{ color: '#999' }}>N/A</span>;
        }

        // Color code based on days pending
        let color = '#52c41a'; // Green for positive (still pending)
        let backgroundColor = '#f6ffed';
        let borderColor = '#b7eb8f';
        
        if (daysPending <= 0) {
          // Red for overdue (0 or negative)
          color = '#ff4d4f';
          backgroundColor = '#fff2f0';
          borderColor = '#ffccc7';
        } else if (daysPending <= 7) {
          // Orange for warning (7 days or less)
          color = '#fa8c16';
          backgroundColor = '#fff7e6';
          borderColor = '#ffd591';
        }

        return (
          <Tag 
            color={color}
            style={{ 
              backgroundColor,
              borderColor,
              color,
              fontWeight: '500',
              minWidth: '60px',
              textAlign: 'center'
            }}
          >
            {daysPending > 0 ? `${daysPending} days` : 'Overdue'}
          </Tag>
        );
      },
      sorter: (a: any, b: any) => {
        const aPending = calculateDaysPending(a) || 0;
        const bPending = calculateDaysPending(b) || 0;
        return aPending - bPending;
      },
    }] : []),
    // Days Since Delivered - Only for Ready to Bill status
    ...(selectedStatus === 'Ready to Bill' ? [{
      title: 'Days Since Delivered',
      key: 'daysSinceDelivered',
      width: 160,
      render: (_: any, record: DemographicsData) => {
        const daysSinceDelivered = calculateDaysSinceDelivered(record);
        
        if (daysSinceDelivered === null) {
          return <span style={{ color: '#999' }}>N/A</span>;
        }

        // Color code based on days since delivery
        let color = '#1890ff'; // Blue for recent
        let backgroundColor = '#e6f7ff';
        let borderColor = '#91d5ff';
        
        if (daysSinceDelivered > 30) {
          // Red for over 30 days
          color = '#ff4d4f';
          backgroundColor = '#fff2f0';
          borderColor = '#ffccc7';
        } else if (daysSinceDelivered > 14) {
          // Orange for over 14 days
          color = '#fa8c16';
          backgroundColor = '#fff7e6';
          borderColor = '#ffd591';
        }
        
        return (
          <Tag 
            color={color}
            style={{ 
              backgroundColor,
              borderColor,
              color,
              fontWeight: '500',
              minWidth: '80px',
              textAlign: 'center'
            }}
          >
            {daysSinceDelivered} {daysSinceDelivered === 1 ? 'day' : 'days'}
          </Tag>
        );
      },
      sorter: (a: any, b: any) => {
        const aDays = calculateDaysSinceDelivered(a) || 0;
        const bDays = calculateDaysSinceDelivered(b) || 0;
        return aDays - bDays;
      },
    }] : []),
    // Payment columns - For Payment Due, Payment Received, and Payment Denied statuses
    ...(['Payment Due', 'Payment Received', 'Payment Denied'].includes(selectedStatus || '') ? [{
      title: 'Primary Amount',
      key: 'primaryAmount',
      width: 140,
      render: (_: any, record: any) => {
        const amount = record.paymentInfo?.primaryAmount;
        return (
          <span style={{ color: amount ? '#1890ff' : '#999', fontWeight: amount ? '500' : 'normal' }}>
            {amount !== null && amount !== undefined ? `$${Number(amount).toFixed(2)}` : '$0'}
          </span>
        );
      },
      sorter: (a: any, b: any) => {
        const aAmount = a.paymentInfo?.primaryAmount || 0;
        const bAmount = b.paymentInfo?.primaryAmount || 0;
        return aAmount - bAmount;
      },
    }] : []),
    ...(['Payment Due', 'Payment Received', 'Payment Denied'].includes(selectedStatus || '') ? [{
      title: 'Secondary Amount',
      key: 'secondaryAmount',
      width: 150,
      render: (_: any, record: any) => {
        const amount = record.paymentInfo?.secondaryAmount;
        return (
          <span style={{ color: amount ? '#52c41a' : '#999', fontWeight: amount ? '500' : 'normal' }}>
            {amount !== null && amount !== undefined ? `$${Number(amount).toFixed(2)}` : '$0'}
          </span>
        );
      },
      sorter: (a: any, b: any) => {
        const aAmount = a.paymentInfo?.secondaryAmount || 0;
        const bAmount = b.paymentInfo?.secondaryAmount || 0;
        return aAmount - bAmount;
      },
    }] : []),
    ...(['Payment Due', 'Payment Received', 'Payment Denied'].includes(selectedStatus || '') ? [{
      title: 'Patient Amount',
      key: 'patientAmount',
      width: 140,
      render: (_: any, record: any) => {
        const amount = record.paymentInfo?.patientAmount;
        return (
          <span style={{ color: amount ? '#722ed1' : '#999', fontWeight: amount ? '500' : 'normal' }}>
            {amount !== null && amount !== undefined ? `$${Number(amount).toFixed(2)}` : '$0'}
          </span>
        );
      },
      sorter: (a: any, b: any) => {
        const aAmount = a.paymentInfo?.patientAmount || 0;
        const bAmount = b.paymentInfo?.patientAmount || 0;
        return aAmount - bAmount;
      },
    }] : []),
    // Invoice Number - Only for Payment Received status
    ...(selectedStatus === 'Payment Received' ? [{
      title: 'Invoice',
      key: 'invoiceNumber',
      width: 150,
      render: (_: any, record: any) => {
        const invoice = invoiceInfo[record.orderId];
        
        if (invoice === undefined) {
          // Still loading
          return <span style={{ color: '#999', fontStyle: 'italic' }}>Loading...</span>;
        }
        
        if (invoice === null) {
          // No invoice found
          return <span style={{ color: '#999' }}>No Invoice</span>;
        }
        
        return (
          <span 
            style={{ 
              color: '#1890ff', 
              fontWeight: '500',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
            onClick={() => {
              navigate('/invoices', {
                state: { invoiceNumber: invoice.invoiceNumber }
              });
            }}
            title="Click to view invoice in Invoices page"
          >
            {invoice.invoiceNumber}
          </span>
        );
      },
      sorter: (a: any, b: any) => {
        const aInvoice = invoiceInfo[a.orderId]?.invoiceNumber || '';
        const bInvoice = invoiceInfo[b.orderId]?.invoiceNumber || '';
        return aInvoice.localeCompare(bInvoice);
      },
    }] : []),
    // Demographics File - Hidden for manufacturers
    ...(user?.role !== 'manufacturer' ? [{
      title: 'Demographics File',
      dataIndex: 'filename',
      key: 'filename',
      width: 200,
      ellipsis: true,
      render: (filename: any, record: any) => {
        if (!filename) {
          return <span style={{ color: '#999' }}>No file</span>;
        }
        return (
          <Button
            type="link"
            onClick={() => handleViewPDF(record.id)}
            style={{ 
              padding: 0, 
              height: 'auto',
              textAlign: 'left',
              width: '100%',
              justifyContent: 'flex-start'
            }}
          >
            {filename}
          </Button>
        );
      },
      sorter: (a, b) => (a.filename || '').localeCompare(b.filename || ''),
    }] : []),
    // {
    //   title: 'On Demand',
    //   dataIndex: 'onDemand',
    //   key: 'onDemand',
    //   width: 120,
    //   render: (onDemand) => {
    //     return (
    //       <div style={{ 
    //         display: 'flex', 
    //         alignItems: 'center', 
    //         gap: '8px',
    //         padding: '6px 10px',
    //         borderRadius: '8px',
    //         minHeight: '32px'
    //       }}>
    //         <Switch
    //           checked={onDemand}
    //           disabled={true}
    //           size="small"
    //         />
    //       </div>
    //     );
    //   },
    //   filters: [
    //     { text: 'Yes', value: true },
    //     { text: 'No', value: false },
    //   ],
    //   onFilter: (value, record) => record.onDemand === value,
    //   // Remove default sorter to maintain our custom sorting
    //   sorter: false,
    // },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 180,
      render: (_, record) => {
        // Show order status instead of demographics status
        let displayStatus = 'Ready to Order'; // Default for Ready to Order records
        
        if (record.orderStatus) {
          // Map order status to display status
          switch (record.orderStatus) {
            case 'ReadyToOrder':
              displayStatus = 'Ready to Order';
              break;
            case 'InProgress':
            case 'YetToBeShipped':
            case 'ReadyToBeShipped':
            case 'Shipped':
              displayStatus = record.orderStatus;
              break;
            case 'Delivered':
              displayStatus = 'Delivered';
              break;
            case 'Completed':
              displayStatus = 'Delivered'; // Show Completed as Delivered in UI
              break;
            case 'Cancelled':
              displayStatus = 'Cancelled';
              break;
            case 'ReadyToBill':
              displayStatus = 'Ready to Bill';
              break;
            case 'PaymentDue':
              displayStatus = 'Payment Due';
              break;
            case 'PaymentDenied':
              displayStatus = 'Payment Denied';
              break;
            case 'PaymentReceived':
              displayStatus = 'Payment Received';
              break;
            default:
              displayStatus = 'Ready to Order';
          }
        } else {
          // For demographics without orders, show "Ready to Order"
          displayStatus = 'Ready to Order';
        }

        const statusConfig = {
          'Ready to Order': { color: '#722ed1', icon: <ClockCircleOutlined />, text: 'Ready to Order' },
          'In Progress': { color: '#1890ff', icon: <ExclamationCircleOutlined />, text: 'In Progress' },
          'InProgress': { color: '#1890ff', icon: <ExclamationCircleOutlined />, text: 'InProgress' },
          'YetToBeShipped': { color: '#fa8c16', icon: <ProductionQuantityLimitsIcon style={{ fontSize: '14px' }} />, text: 'YetToBeShipped' },
          'ReadyToBeShipped': { color: '#1890ff', icon: <ExclamationCircleOutlined />, text: 'ReadyToBeShipped' },
          'Shipped': { color: '#13c2c2', icon: <TruckOutlined />, text: 'Shipped' },
          'Delivered': { color: '#52c41a', icon: <CheckCircleOutlined />, text: 'Delivered' },
          'Completed': { color: '#52c41a', icon: <CheckCircleOutlined />, text: 'Delivered' }, // Show as Delivered
          'Cancelled': { color: '#ff4d4f', icon: <CloseCircleOutlined />, text: 'Cancelled' },
          'Ready to Bill': { color: '#722ed1', icon: <FileTextOutlined />, text: 'Ready to Bill' },
          'Payment Due': { color: '#fa8c16', icon: <ExclamationCircleOutlined />, text: 'Payment Due' },
          'Payment Denied': { color: '#ff4d4f', icon: <CloseCircleOutlined />, text: 'Payment Denied' },
          'Payment Received': { color: '#52c41a', icon: <CheckCircleOutlined />, text: 'Payment Received' }
        };

        const config = statusConfig[displayStatus as keyof typeof statusConfig] || statusConfig['Ready to Order'];

        // Make Shipped status clickable to redirect to UPS tracking
        const isShipped = displayStatus === 'Shipped' && record.trackingNumber;
        
        return (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            padding: '6px 10px',
            borderRadius: '8px',
            minHeight: '32px'
          }}>
            <Tag 
              color={config.color} 
              style={{ 
                margin: 0,
                backgroundColor: displayStatus === 'Shipped' ? '#e6f7ff' : 
                                displayStatus === 'Delivered' ? '#f6ffed' :
                                displayStatus === 'Ready to Order' ? '#f9f0ff' : undefined,
                borderColor: displayStatus === 'Shipped' ? '#13c2c2' : 
                            displayStatus === 'Delivered' ? '#52c41a' :
                            displayStatus === 'Ready to Order' ? '#722ed1' :
                            displayStatus === 'InProgress' ? '#1890ff' :
                            displayStatus === 'YetToBeShipped' ? '#fa8c16' :
                            displayStatus === 'ReadyToBeShipped' ? '#1890ff' :
                            displayStatus === 'Cancelled' ? '#ff4d4f' :
                            displayStatus === 'Ready to Bill' ? '#722ed1' :
                            displayStatus === 'Payment Due' ? '#fa8c16' :
                            displayStatus === 'Payment Denied' ? '#ff4d4f' :
                            displayStatus === 'Payment Received' ? '#52c41a' : undefined,
                color: displayStatus === 'Delivered' ? '#52c41a' : 
                       displayStatus === 'Shipped' ? '#13c2c2' :
                       displayStatus === 'Ready to Order' ? '#722ed1' :
                       displayStatus === 'InProgress' ? '#1890ff' :
                       displayStatus === 'YetToBeShipped' ? '#fa8c16' :
                       displayStatus === 'ReadyToBeShipped' ? '#1890ff' :
                       displayStatus === 'Cancelled' ? '#ff4d4f' :
                       displayStatus === 'Ready to Bill' ? '#722ed1' :
                       displayStatus === 'Payment Due' ? '#fa8c16' :
                       displayStatus === 'Payment Denied' ? '#ff4d4f' :
                       displayStatus === 'Payment Received' ? '#52c41a' : undefined,
                cursor: isShipped ? 'pointer' : 'default',
                textDecoration: isShipped ? 'underline' : 'none'
              }}
              onClick={isShipped ? () => {
                if (record.trackingNumber) {
                  handleTrackingNumberClick(record.trackingNumber, record.deliveryPartnerId);
                }
              } : undefined}
            >
              {config.icon}
              <span style={{ marginLeft: '4px' }}>{config.text}</span>
            </Tag>
          </div>
        );
      },
      filters: [
        { text: 'Ready to Order', value: 'Ready to Order' },
        { text: 'InProgress', value: 'InProgress' },
        { text: 'YetToBeShipped', value: 'YetToBeShipped' },
        { text: 'ReadyToBeShipped', value: 'ReadyToBeShipped' },
        { text: 'Shipped', value: 'Shipped' },
        { text: 'Delivered', value: 'Delivered' },
        { text: 'Completed', value: 'Completed' },
        { text: 'Cancelled', value: 'Cancelled' },
      ],
      onFilter: (value, record) => {
        // Filter based on the display status logic
        let displayStatus = 'Ready to Order';
        if (record.orderStatus) {
          switch (record.orderStatus) {
            case 'ReadyToOrder':
              displayStatus = 'Ready to Order';
              break;
            case 'InProgress':
            case 'YetToBeShipped':
            case 'ReadyToBeShipped':
            case 'Shipped':
              displayStatus = record.orderStatus;
              break;
            case 'Delivered':
              displayStatus = 'Delivered';
              break;
            case 'Completed':
              displayStatus = 'Delivered'; // Show Completed as Delivered in UI
              break;
            case 'Cancelled':
              displayStatus = 'Cancelled';
              break;
            case 'ReadyToBill':
              displayStatus = 'Ready to Bill';
              break;
            case 'PaymentDue':
              displayStatus = 'Payment Due';
              break;
            case 'PaymentDenied':
              displayStatus = 'Payment Denied';
              break;
            case 'PaymentReceived':
              displayStatus = 'Payment Received';
              break;
            default:
              displayStatus = 'Ready to Order';
          }
        } else {
          displayStatus = 'Ready to Order';
        }
        return displayStatus === value;
      },
      sorter: (a, b) => {
        // Sort based on display status
        const getDisplayStatus = (record: any) => {
          if (record.orderStatus) {
            switch (record.orderStatus) {
              case 'ReadyToOrder':
                return 'Ready to Order';
              case 'InProgress':
              case 'YetToBeShipped':
              case 'ReadyToBeShipped':
              case 'Shipped':
                return record.orderStatus;
              case 'Completed':
                return 'Completed';
              case 'Cancelled':
                return 'Cancelled';
              default:
                return 'Ready to Order';
            }
          } else {
            return 'Ready to Order';
          }
        };
        
        return getDisplayStatus(a).localeCompare(getDisplayStatus(b));
      },
    },
         {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_, record) => {
        // Build menu items dynamically based on record status and user role
        const menuItems: any[] = [];

        // // Start Verification - Only for Pending records
        // if (record.status === 'Pending') {
        //   menuItems.push(
        //     <Menu.Item 
        //       key="startVerification"
        //       icon={<CaretRightOutlined />}
        //       onClick={() => {
        //         Modal.confirm({
        //           title: 'Start Verification',
        //           content: 'Are you sure you want to start verification for this record? This will change the status to "In Progress".',
        //           okText: 'Yes, Start Verification',
        //           cancelText: 'Cancel',
        //           onOk: () => handleStartVerification(record.id)
        //         });
        //       }}
        //     >
        //       Start Verification
        //     </Menu.Item>
        //   );
        // }

        // Create Order - Only for Accepted records when Ready to Order status is selected and user is not manufacturer
        if (record.status === 'Accepted' && selectedStatus === 'Ready to Order' && user?.role !== 'manufacturer') {
          menuItems.push(
            <Menu.Item 
              key="createOrder"
              icon={<PlusOutlined />}
              onClick={() => {
                Modal.confirm({
                  title: 'Create Order',
                  content: 'Are you sure you want to create an order for this accepted record?',
                  okText: 'Yes, Create Order',
                  cancelText: 'Cancel',
                  onOk: () => handleCreateOrder(record.id, record)
                });
              }}
          
            >
              Create Order
            </Menu.Item>
          );
          
            menuItems.push(
              <Menu.Item 
                key="cancelOrder"
                icon={<CloseCircleOutlined />}
                onClick={() => handleCancelReadyToOrder(record.id)}
                // style={{ color: '#ff4d4f' }}
              >
                Cancel Order
              </Menu.Item>
            );
          
        }

        // Order Management Actions - For order records
        if (currentData === 'orders' || (record.orderStatus && !['ReadyToOrder'].includes(record.orderStatus))) {
          // View Order
          menuItems.push(
            <Menu.Item 
              key="viewOrder"
              icon={<FileTextOutlined />}
              onClick={() => handleViewOrder(record.orderId || record.id)}
            >
              View Order
            </Menu.Item>
          );

          // For In Progress orders (excluding ReadyToOrder, Completed, Cancelled)
          // Order Form - Only for non-manufacturer users
          if (record.orderStatus && !['ReadyToOrder','Cancelled'].includes(record.orderStatus) && user?.role !== 'manufacturer') {
            menuItems.push(
              <Menu.Item 
                key="orderForm"
                icon={<FileTextOutlined />}
                onClick={() => handleOrderForm(record.orderId || record.id)}
              >
                Order Form
              </Menu.Item>
            );
          }

          if (user?.role !== 'manufacturer' && record.orderStatus === 'YetToBeShipped') {
            menuItems.push(
              <Menu.Item 
                key="editOrder"
                icon={<EditOutlined />}
                onClick={() => handleEditOrder(record.orderId || record.id, record.orderStatus)}
              >
                Edit Order
              </Menu.Item>
            );
          }
            if (user?.role === 'manufacturer' && (['YetToBeShipped', 'Shipped'].includes(record.orderStatus))) {
              menuItems.push(
                <Menu.Item 
                  key="updateStatus"
                  icon={<SyncOutlined />}
                  onClick={() => handleUpdateOrderStatus(record)}
                >
                  Update Status
                </Menu.Item>
              );
            }
            if (user?.role !== 'manufacturer' && record.orderStatus === 'YetToBeShipped') {
            menuItems.push(
              <Menu.Item 
                key="cancelOrder"
                icon={<CloseCircleOutlined />}
                onClick={() => handleCancelOrder(record.orderId || record.id)}
                // style={{ color: '#ff4d4f' }}
              >
                Cancel Order
              </Menu.Item>
            );
          }
          // console.log("Order Status: ", record.orderStatus);
          // // For Cancelled orders
          // if (record.orderStatus === 'Cancelled') {
          //   menuItems.push(
          //     <Menu.Item 
          //       key="reorderOrder"
          //       icon={<ReloadOutlined />}
          //       onClick={() => handleReorderOrder(record.orderId || record.id, record.id)}
          //       style={{ color: '#52c41a' }}
          //     >
          //       Reorder
          //     </Menu.Item>
          //   );
          // }

          // Doc Package - For Delivered and Completed orders
          if (['Delivered', 'PaymentReceived','PaymentDenied','ReadyToBill','PaymentDue'].includes(record.orderStatus)) {
            menuItems.push(
              <Menu.Item 
                key="docPackage"
                icon={<FileTextOutlined />}
                onClick={() => handleDocPackage(record.orderId || record.id)}
              >
                View Doc Package
              </Menu.Item>
            );
          }

          // Update Status to Ready to Bill - For Delivered orders (practitioners only)
          if (record.orderStatus === 'Delivered' && user?.role !== 'manufacturer') {
            menuItems.push(
              <Menu.Item 
                key="updateStatusToBill"
                icon={<SyncOutlined />}
                onClick={() => handleUpdateOrderStatus(record)}
              >
                Update Status
              </Menu.Item>
            );
          }

          // Claim Form Actions - For Ready to Bill orders
          if (record.orderStatus === 'ReadyToBill' && user?.role !== 'manufacturer') {
            menuItems.push(
              <Menu.Item 
                key="uploadClaimForm"
                icon={<UploadOutlined />}
                onClick={() => handleUploadClaimForm(record.orderId || record.id)}
              >
                Upload Claim Form
              </Menu.Item>
            );
            
            menuItems.push(
              <Menu.Item 
                key="viewClaimForm"
                icon={<FileTextOutlined />}
                onClick={() => handleViewClaimForm(record.orderId || record.id)}
              >
                View Claim Form
              </Menu.Item>
            );
            
            menuItems.push(
              <Menu.Item 
                key="markAsBilled"
                icon={<CheckCircleOutlined />}
                onClick={() => handleMarkAsBilled(record.orderId || record.id)}
              >
                Mark as Billed
              </Menu.Item>
            );
          }

          // Payment Actions - For Payment Due, Payment Received, and Payment Denied orders
          if (['PaymentDue', 'PaymentReceived', 'PaymentDenied'].includes(record.orderStatus) && user?.role !== 'manufacturer') {
            menuItems.push(
              <Menu.Item 
                key="addPayment"
                icon={<PlusOutlined />}
                onClick={() => handleAddPayment(record)}
              >
                Add Payment
              </Menu.Item>
            );
            
            menuItems.push(
              <Menu.Item 
                key="uploadEobForm"
                icon={<UploadOutlined />}
                onClick={() => handleUploadEobForm(record.orderId || record.id)}
              >
                Upload EOB Form
              </Menu.Item>
            );
            
            menuItems.push(
              <Menu.Item 
                key="viewEobForm"
                icon={<FileTextOutlined />}
                onClick={() => handleViewEobForm(record.orderId || record.id)}
              >
                View EOB Form
              </Menu.Item>
            );
            
            menuItems.push(
              <Menu.Item 
                key="updatePaymentStatus"
                icon={<SyncOutlined />}
                onClick={() => handleUpdateOrderStatus(record)}
              >
                Update Status
              </Menu.Item>
            );
          }

          // Submit Order - For Ready to Order status
          if (record.status === 'Ready to Order') {
            menuItems.push(
              <Menu.Item 
                key="submitOrder"
                icon={<EditOutlined />}
                onClick={() => navigate(`/submitOrder/order/${record.orderId || record.id}`, {
                  state: { recordData: record }
                })}
                style={{ color: '#722ed1' }}
              >
                Submit Order
              </Menu.Item>
            );
          }
        }
        
        if (record.orderStatus === 'Cancelled' && user?.role !== 'manufacturer') {
          menuItems.push(
            <Menu.Item 
              key="reorderOrder"
              icon={<ReloadOutlined />}
              onClick={() => handleReorderOrder(record.orderId || record.id, record.id)}
              // style={{ color: '#52c41a' }}
            >
              Reorder
            </Menu.Item>
          );
        }

        // // Insurance Verification Actions - Only for In Progress records
        // if (record.status === 'In Progress') {
        //   menuItems.push(
        //     <Menu.Item 
        //       key="uploadInsurance"
        //       icon={<UploadOutlined />}
        //       onClick={() => {
        //         setSelectedRecordForInsurance(record);
        //         setIsInsuranceUploadModalVisible(true);
        //       }}
        //     >
        //       Upload Insurance File
        //     </Menu.Item>
        //   );

          // // Show Accept/Reject buttons only if insurance file is uploaded
          // if (insuranceFileInfo[record.id]?.hasInsuranceFile) {
          //   // menuItems.push(
          //   //   <Menu.Item 
          //   //     key="acceptInsurance"
          //   //     icon={<CheckCircleOutlined />}
          //   //     onClick={() => {
          //   //       Modal.confirm({
          //   //         title: 'Accept Insurance Verification',
          //   //         content: 'Are you sure you want to accept this insurance verification?',
          //   //         okText: 'Yes, Accept',
          //   //         cancelText: 'Cancel',
          //   //         onOk: () => handleAcceptInsurance(record.id)
          //   //       });
          //   //     }}
          //   //     style={{ color: '#52c41a' }}
          //   //   >
          //   //     Accept Insurance
          //   //   </Menu.Item>
          //   // );

          //   // menuItems.push(
          //   //   <Menu.Item 
          //   //     key="rejectInsurance"
          //   //     icon={<CloseCircleOutlined />}
          //   //     onClick={() => handleRejectInsurance(record.id)}
          //   //     style={{ color: '#ff4d4f' }}
          //   //   >
          //   //     Reject Insurance
          //   //   </Menu.Item>
          //   // );

          //   menuItems.push(
          //     <Menu.Item 
          //       key="viewInsuranceFile"
          //       icon={<FileTextOutlined />}
          //       onClick={() => handleViewInsuranceFile(record.id)}
          //     >
          //       View Insurance File
          //     </Menu.Item>
          //   );
          // }
        

        // View Insurance File - For Accepted and Rejected records (not for manufacturers)
        if ((record.status === 'Accepted' || record.status === 'Rejected') && user?.role !== 'manufacturer') {
          menuItems.push(
            <Menu.Item 
              key="viewInsuranceFile"
              icon={<FileTextOutlined />}
              onClick={() => handleViewInsuranceFile(record.id)}
            >
              View Insurance File
            </Menu.Item>
          );
        }

        // Order Notes - Available for Practitioner, Manufacturer, and Admin
        if (user?.role === 'practitioner' || user?.role === 'manufacturer' || user?.role === 'admin') {
          menuItems.push(
            <Menu.Item 
              key="orderNotes"
              icon={<FileTextOutlined />}
              onClick={() => handleOpenNotesModal(record)}
            >
              Order Notes
            </Menu.Item>
          );
        }
      

        return (
          <Dropdown
            overlay={<Menu>{menuItems}</Menu>}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="default"
              size="small"
              icon={<MoreOutlined />}
              style={{ 
                padding: '4px 8px',
                minWidth: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Actions"
            />
          </Dropdown>
        );
      },
    },
  ];

  // Columns for rejected records
  const rejectedColumns: ColumnsType<DemographicsData> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: (a, b) => Number(a.id) - Number(b.id),
    },
    {
      title: 'Rejected Reason',
      dataIndex: 'rejectedReason',
      key: 'rejectedReason',
      width: 200,
      render: (text) => text || 'No reason provided',
      ellipsis: true,
    },
    {
      title: 'Patient Name',
      key: 'patientName',
      width: 80,
      render: (_, record) => {
        const fullName = [record.firstName, record.middleName, record.lastName]
          .filter(name => name && name.trim())
          .join(' ');
        return fullName || 'No name provided';
      },
      sorter: (a, b) => {
        const nameA = [a.firstName, a.middleName, a.lastName].filter(name => name && name.trim()).join(' ');
        const nameB = [b.firstName, b.middleName, b.lastName].filter(name => name && name.trim()).join(' ');
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: 'Rejected By',
      dataIndex: 'rejectedBy',
      key: 'rejectedBy',
      width: 100,
      render: (text) => text || 'Unknown',
    },
    {
      title: 'Filename',
      dataIndex: 'filename',
      key: 'filename',
      width: 100,
      ellipsis: true,
      render: (text, record) => (
        <Button
          type="link"
          onClick={() => handleViewPDF(record.id)}
          style={{ padding: 0, height: 'auto' }}
        >
          {text}
        </Button>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_, record) => {
        const menuItems: any[] = [];

        // View Insurance File
        menuItems.push(
          <Menu.Item 
            key="viewInsuranceFile"
            icon={<FileTextOutlined />}
            onClick={() => handleViewInsuranceFile(record.id)}
          >
            View Insurance File
          </Menu.Item>
        );

        // Order Notes - Available for Practitioner, Manufacturer, and Admin
        if (user?.role === 'practitioner' || user?.role === 'manufacturer' || user?.role === 'admin') {
          menuItems.push(
            <Menu.Item 
              key="orderNotes"
              icon={<FileTextOutlined />}
              onClick={() => handleOpenNotesModal(record)}
              style={{ color: '#722ed1' }}
            >
              Order Notes
            </Menu.Item>
          );
        }

        return (
          <Dropdown
            overlay={<Menu>{menuItems}</Menu>}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="default"
              size="small"
              icon={<MoreOutlined />}
              style={{ 
                padding: '4px 8px',
                minWidth: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Actions"
            />
          </Dropdown>
        );
      },
    },
  ];

  // Modal form handlers
  const handleFileChange = (info: any) => {
    const file = info.file;
    if (file) {
      const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv'];
      if (allowedTypes.includes(file.type)) {
        setFileState(prev => ({ ...prev, selectedFile: file, uploadStatus: '', uploadError: null }));
      } else {
        message.error('Invalid file type. Only PDF, Excel, and CSV files are allowed.');
        setFileState(prev => ({ ...prev, selectedFile: null, uploadError: null }));
      }
    }
  };

  const handleDoctorChange = (value: string) => {
    setFileState(prev => ({ ...prev, selectedDoctor: value, selectedProcedure: '', uploadError: null }));
    // Fetch procedures for the selected doctor
    if (value) {
      fetchProceduresByDoctor(value);
    } else {
      setAvailableProcedures([]);
    }
  };

  const handleProcedureChange = (value: string) => {
    setFileState(prev => ({ ...prev, selectedProcedure: value, uploadError: null }));
  };

  const handleToggleChange = (checked: boolean) => {
    setFileState(prev => ({ ...prev, enableOnDemand: checked, uploadError: null }));
  };

  const handleModalSubmit = async () => {
    if (!fileState.selectedFile) {
      message.error('Please select a file first.');
      return;
    }

    if (!fileState.selectedDoctor) {
      message.error('Please select a doctor.');
      return;
    }

    if (!fileState.selectedProcedure) {
      message.error('Please select a procedure.');
      return;
    }

    if (!fileState.surgeryDate) {
      message.error('Please select a surgery date.');
      return;
    }

    setFileState(prev => ({ ...prev, isUploading: true, uploadError: null }));

    // Get the doctor name for display
    const selectedDoctor = doctors.find(d => d.employeeId === fileState.selectedDoctor);
    const doctorName = selectedDoctor ? selectedDoctor.name : fileState.selectedDoctor;

    const formData = new FormData();
    formData.append('patientDetails', fileState.selectedFile);
    formData.append('doctor', doctorName); // Send doctor name for backward compatibility
    formData.append('doctorId', fileState.selectedDoctor); // Send doctor ID for future use
    formData.append('procedure', fileState.selectedProcedure);
    formData.append('surgeryDate', fileState.surgeryDate.format('YYYY-MM-DD'));
    formData.append('enableOnDemand', fileState.enableOnDemand.toString());

    try {
      const token = getAuthToken();
      const response = await fetch(`${BACKEND_URL}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
      });

             if (response.ok) {
         let responseData;
         try {
           responseData = await response.json();
         } catch (parseError) {
           // If response is not JSON, treat it as plain text success
           responseData = { message: 'File uploaded successfully', isDuplicate: false };
         }

         if (responseData.isDuplicate || responseData.warning) {
           // Show warning for duplicates or surgery date issues
           message.warning(responseData.warning || 'Potential Duplicate Found, Please check the duplicates tab.');
         } else {
           message.success(responseData.message || 'File uploaded successfully!');
         }

         setFileState({
           selectedFile: null,
           isUploading: false,
           uploadStatus: '',
           selectedDoctor: '',
           selectedProcedure: '',
           surgeryDate: null,
           enableOnDemand: false,
           uploadError: null
         });
         setIsModalVisible(false);
         fetchData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || ''); // Refresh data (includes stats)
               } else {
          // Handle backend errors properly
          let errorMessage = 'Upload failed. Please try again.';
          let validationErrors: string[] = [];

          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.error || errorMessage;

            // Check if there are validation errors
            if (errorData.validationErrors && Array.isArray(errorData.validationErrors)) {
              validationErrors = errorData.validationErrors;
            }
          } catch (parseError) {
            // If error response is not JSON, try to get text
            try {
              const errorText = await response.text();
              errorMessage = errorText || errorMessage;
            } catch (textError) {
              // If all else fails, use default message
              console.error('Could not parse error response:', textError);
            }
          }

          // Display validation errors if they exist
          if (validationErrors.length > 0) {
            const validationMessage = `File validation failed:\n${validationErrors.map(error => `• ${error}`).join('\n')}`;
            message.error({
              content: validationMessage,
              duration: 8, // Show longer for validation errors
              style: { whiteSpace: 'pre-line' }
            });
            errorMessage = validationMessage;
          } else {
            message.error(errorMessage);
          }

          // Set error state for display in modal
          setFileState(prev => ({ ...prev, uploadError: errorMessage }));
        }
    } catch (error) {
      console.error('Upload error:', error);
      if (error instanceof Error) {
        message.error(`Upload failed: ${error.message}`);
      } else {
        message.error('Upload failed. Please check your connection.');
      }
    } finally {
      setFileState(prev => ({ ...prev, isUploading: false }));
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    clearFileState();
  };

  const clearFileState = () => {
    setFileState({
      selectedFile: null,
      isUploading: false,
      uploadStatus: '',
      selectedDoctor: '',
      selectedProcedure: '',
      surgeryDate: null,
      enableOnDemand: false,
      uploadError: null
    });
  };

  const clearResubmitFileState = () => {
    setResubmitFileState({
      selectedFile: null,
      isUploading: false,
      uploadStatus: '',
      selectedDoctor: '',
      selectedProcedure: '',
      surgeryDate: null,
      enableOnDemand: false,
      uploadError: null
    });
  };

  // Handle resubmit modal submit
  const handleResubmitSubmit = async () => {
    if (!resubmitRecord) return;

    if (!resubmitFileState.selectedDoctor) {
      message.error('Please select a doctor.');
      return;
    }

    if (!resubmitFileState.selectedProcedure) {
      message.error('Please select a procedure.');
      return;
    }

    if (!resubmitFileState.surgeryDate) {
      message.error('Please select a surgery date.');
      return;
    }

    setResubmitFileState(prev => ({ ...prev, isUploading: true, uploadError: null }));

    // Get the doctor name for display
    const selectedDoctor = doctors.find(d => d.employeeId === resubmitFileState.selectedDoctor);
    const doctorName = selectedDoctor ? selectedDoctor.name : resubmitFileState.selectedDoctor;

    const formData = new FormData();

    // Only append file if a new one is selected
    if (resubmitFileState.selectedFile) {
      formData.append('patientDetails', resubmitFileState.selectedFile);
    }

    formData.append('doctor', doctorName);
    formData.append('doctorId', resubmitFileState.selectedDoctor);
    formData.append('procedure', resubmitFileState.selectedProcedure);
    formData.append('surgeryDate', resubmitFileState.surgeryDate.format('YYYY-MM-DD'));
    formData.append('enableOnDemand', resubmitFileState.enableOnDemand.toString());
    formData.append('keepExistingFile', (!resubmitFileState.selectedFile).toString());

    try {
      const token = getAuthToken();
      const response = await fetch(`${BACKEND_URL}/resubmit/${resubmitRecord.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
      });

      if (response.ok) {
        let responseData;
        try {
          responseData = await response.json();
        } catch (parseError) {
          responseData = { message: 'Record resubmitted successfully', isDuplicate: false };
        }

        if (responseData.isDuplicate || responseData.warning) {
          message.warning(responseData.warning || 'Potential Duplicate Found, Please check the duplicates tab.');
        } else {
          message.success('Record resubmitted successfully!');
        }

        clearResubmitFileState();
        setIsResubmitModalVisible(false);
        setResubmitRecord(null);
        fetchData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || ''); // Refresh data (includes stats)
      } else {
        let errorMessage = 'Resubmit failed. Please try again.';
        let validationErrors: string[] = [];

        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
          validationErrors = errorData.errors || [];
        } catch (parseError) {
          // If response is not JSON, use the status text
          errorMessage = response.statusText || errorMessage;
        }

        if (validationErrors.length > 0) {
          message.error(
            <div>
              <div>{errorMessage}</div>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          );
        } else {
          message.error(errorMessage);
        }
      }
    } catch (error) {
      console.error('Resubmit error:', error);
      message.error('Network error. Please check your connection and try again.');
    } finally {
      setResubmitFileState(prev => ({ ...prev, isUploading: false }));
    }
  };

  // Handle resubmit modal cancel
  const handleResubmitCancel = () => {
    setIsResubmitModalVisible(false);
    clearResubmitFileState();
    setResubmitRecord(null);
  };


  // Handle closing notes modal
  const handleCloseNotesModal = () => {
    setIsNotesModalVisible(false);
    setSelectedRecord(null);
    setNotes([]);
    setNewNoteText('');
  };

  // Handle opening notes modal
  const handleOpenNotesModal = (record: DemographicsData) => {
    setSelectedRecord(record);
    setIsNotesModalVisible(true);
    setNewNoteText('');
    fetchNotes(String(record.id));
  };

  // Check if notes should be read-only (currently allowing all statuses to have editable notes)
  const isNotesReadOnly = (_record: DemographicsData) => {
    return false; // Allow notes to be added/edited for all statuses
  };

  // Check if user can delete a specific note
  const canDeleteNote = (note: Note) => {
    // Check if current user's name matches the note creator's name, or if user is admin
    return user?.name === note.addedBy || user?.role === 'admin';
  };

  // Handle adding a new note
  const handleAddNote = async () => {
    if (!newNoteText.trim() || !selectedRecord) return;
    await addNote(String(selectedRecord.id), newNoteText.trim());
  };


  return (
    <div className="dashboard-page">
             {/* First Container - Search and Add Button (15% height) */}
       <div className="search-container">
         <div className="search-content" style={{ position: 'relative', zIndex: 1 }}>
          <div className="search-inputs" style={{ pointerEvents: 'auto', zIndex: 5, flex: 1 }}>
            <OrdersSmartSearch
              onSearch={handleSmartSearch}
              style={{ marginBottom: 16, pointerEvents: 'auto' }}
            />

          </div>
         </div>
       </div>

      {/* Status Statistics Container */}
      <div className="status-stats-container" style={{ 
        padding: '16px', 
        backgroundColor: '#f5f5f5', 
        marginBottom: '16px',
        borderRadius: '8px'
      }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '16px' 
        }}>


          {/* Ready to Order Status Box */}
          <Card
            hoverable
            onClick={() => handleStatusFilterClick('Ready to Order')}
            style={{
              cursor: 'pointer',
              border: selectedStatus === 'Ready to Order' ? '2px solid #faad14' : '1px solid #d9d9d9',
              backgroundColor: selectedStatus === 'Ready to Order' ? '#fffbe6' : '#fff'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '50%', 
                backgroundColor: '#faad14',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '16px'
              }}>
                <FileClock size={20} />
              </div>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '14px' }}>Ready to Order</div>
                <div style={{ color: '#666', fontSize: '12px' }}>{statusStats['Ready to Order']} records</div>
              </div>
            </div>
          </Card>

          {/* In Progress Status Box */}
          <Card
            hoverable
            onClick={() => handleStatusFilterClick('In Progress')}
            style={{
              cursor: 'pointer',
              border: selectedStatus === 'In Progress' ? '2px solid #1890ff' : '1px solid #d9d9d9',
              backgroundColor: selectedStatus === 'In Progress' ? '#e6f7ff' : '#fff'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '50%', 
                backgroundColor: '#1890ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '16px'
              }}>
                <Loader size={20} />
              </div>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '14px' }}>In Progress</div>
                <div style={{ color: '#666', fontSize: '12px' }}>{statusStats['In Progress']} records</div>
              </div>
            </div>
          </Card>

          {/* Delivered Status Box */}
          <Card
            hoverable
            onClick={() => handleStatusFilterClick('Delivered')}
            style={{
              cursor: 'pointer',
              border: selectedStatus === 'Delivered' ? '2px solid #52c41a' : '1px solid #d9d9d9',
              backgroundColor: selectedStatus === 'Delivered' ? '#f6ffed' : '#fff'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '50%', 
                backgroundColor: '#52c41a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '16px'
              }}>
                <FileCheck size={20} />
              </div>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '14px' }}>Delivered</div>
                <div style={{ color: '#666', fontSize: '12px' }}>{statusStats['Delivered']} records</div>
              </div>
            </div>
          </Card>

          {/* Cancelled Status Box */}
          <Card
            hoverable
            onClick={() => handleStatusFilterClick('Cancelled')}
            style={{
              cursor: 'pointer',
              border: selectedStatus === 'Cancelled' ? '2px solid #ff4d4f' : '1px solid #d9d9d9',
              backgroundColor: selectedStatus === 'Cancelled' ? '#fff2f0' : '#fff'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '50%', 
                backgroundColor: '#ff4d4f',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '16px'
              }}>
                <CircleX size={20} />
              </div>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '14px' }}>Cancelled</div>
                <div style={{ color: '#666', fontSize: '12px' }}>{statusStats['Cancelled']} records</div>
              </div>
            </div>
          </Card>

          {/* Ready to Bill Status Box */}
          <Card
            hoverable
            onClick={() => handleStatusFilterClick('Ready to Bill')}
            style={{
              cursor: 'pointer',
              border: selectedStatus === 'Ready to Bill' ? '2px solid #722ed1' : '1px solid #d9d9d9',
              backgroundColor: selectedStatus === 'Ready to Bill' ? '#f9f0ff' : '#fff'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '50%', 
                backgroundColor: '#722ed1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '16px'
              }}>
                <FileTextOutlined style={{ fontSize: '20px' }} />
              </div>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '14px' }}>Ready to Bill</div>
                <div style={{ color: '#666', fontSize: '12px' }}>{statusStats['Ready to Bill']} records</div>
              </div>
            </div>
          </Card>

          {/* Payment Due Status Box */}
          <Card
            hoverable
            onClick={() => handleStatusFilterClick('Payment Due')}
            style={{
              cursor: 'pointer',
              border: selectedStatus === 'Payment Due' ? '2px solid #fa8c16' : '1px solid #d9d9d9',
              backgroundColor: selectedStatus === 'Payment Due' ? '#fff7e6' : '#fff'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '50%', 
                backgroundColor: '#fa8c16',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '16px'
              }}>
                <ExclamationCircleOutlined style={{ fontSize: '20px' }} />
              </div>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '14px' }}>Payment Due</div>
                <div style={{ color: '#666', fontSize: '12px' }}>{statusStats['Payment Due']} records</div>
              </div>
            </div>
          </Card>

          {/* Payment Denied Status Box */}
          <Card
            hoverable
            onClick={() => handleStatusFilterClick('Payment Denied')}
            style={{
              cursor: 'pointer',
              border: selectedStatus === 'Payment Denied' ? '2px solid #ff4d4f' : '1px solid #d9d9d9',
              backgroundColor: selectedStatus === 'Payment Denied' ? '#fff2f0' : '#fff'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '50%', 
                backgroundColor: '#ff4d4f',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '16px'
              }}>
                <CloseCircleOutlined style={{ fontSize: '20px' }} />
              </div>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '14px' }}>Payment Denied</div>
                <div style={{ color: '#666', fontSize: '12px' }}>{statusStats['Payment Denied']} records</div>
              </div>
            </div>
          </Card>

          {/* Payment Received Status Box */}
          <Card
            hoverable
            onClick={() => handleStatusFilterClick('Payment Received')}
            style={{
              cursor: 'pointer',
              border: selectedStatus === 'Payment Received' ? '2px solid #52c41a' : '1px solid #d9d9d9',
              backgroundColor: selectedStatus === 'Payment Received' ? '#f6ffed' : '#fff'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '50%', 
                backgroundColor: '#52c41a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '16px'
              }}>
                <CheckCircleOutlined style={{ fontSize: '20px' }} />
              </div>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '14px' }}>Payment Received</div>
                <div style={{ color: '#666', fontSize: '12px' }}>{statusStats['Payment Received']} records</div>
              </div>
            </div>
          </Card>
        </div>
      </div>

                           {/* Second Container - Table (remaining height) */}
        <div className="table-container">
          {/* Action Buttons Row */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '16px',
            gap: '16px'
          }}>
            <div style={{ display: 'flex', gap: '8px' }}>
            </div>

            <div style={{ display: 'flex', gap: '8px', position: 'relative' }}>
              {/* Columns Button */}
              <Button
                icon={<SettingOutlined />}
                onClick={() => setShowColumnsDropdown(!showColumnsDropdown)}
                size="middle"
                type="default"
                title="Column settings"
                data-columns-button
              >
                Columns
              </Button>

              {/* Columns Dropdown Menu */}
              {showColumnsDropdown && (
                <div
                  data-columns-dropdown
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    zIndex: 1000,
                    backgroundColor: 'white',
                    border: '1px solid #d9d9d9',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    padding: '12px',
                    minWidth: '220px',
                    maxHeight: '400px',
                    overflowY: 'auto',
                    marginTop: '4px'
                  }}
                >
                  <div style={{ 
                    fontSize: '14px', 
                    fontWeight: 'bold', 
                    marginBottom: '12px',
                    color: '#262626',
                    borderBottom: '1px solid #f0f0f0',
                    paddingBottom: '8px'
                  }}>
                    Column Visibility
                  </div>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={columnOrder}
                      strategy={verticalListSortingStrategy}
                    >
                      <div style={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px'
                      }}>
                        {getOrderedColumnConfig().map((column) => (
                          <SortableItem
                            key={column.key}
                            id={column.key}
                            label={column.label}
                            checked={columnVisibility[column.key]}
                            onToggle={handleColumnVisibilityChange}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>
              )}

              {/* Clear Table Filters Button */}
              <Button
                icon={<ClearOutlined />}
                onClick={handleClearTableFilters}
                size="middle"
                type="default"
                title="Clear table column filters and sorting"
              >
                Clear Table Filters
              </Button>
            </div>
          </div>

                     <Card title="Orders Data" className="table-card">
                                                <Table
                key={tableKey}
                ref={tableRef}
                columns={selectedStatus === 'Rejected' ? (rejectedColumns as any) : (getVisibleColumns(columns) as any)}
                dataSource={filteredData}
                rowKey="id"
                loading={loading}
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: totalCount,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  pageSizeOptions: [10, 20, 50, 100],
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                  onChange: (page, size) => {
                    setCurrentPage(page);
                    setPageSize(size || 10);
                  },
                }}
                scroll={{ x: 1200 }}
                size="middle"
              />
          </Card>
       </div>

      {/* Upload Modal */}
      <Modal
        title="Add New Demographics"
        open={isModalVisible}
        onOk={handleModalSubmit}
        onCancel={handleModalCancel}
        width={600}
        confirmLoading={fileState.isUploading}
        okText="Upload"
        cancelText="Cancel"
      >
        <div className="modal-form">
          <div className="form-item">
            <label>Select Doctor:</label>
            <Select
              placeholder="Choose a doctor..."
              value={fileState.selectedDoctor}
              onChange={handleDoctorChange}
              style={{ width: '100%' }}
              loading={loadingDoctors}
            >
              {doctors.map((doctor, index) => (
                <Option key={index} value={doctor.employeeId}>{doctor.name}</Option>
              ))}
            </Select>
          </div>

          <div className="form-item">
            <label>Select Procedure:</label>
            <Select
              placeholder={fileState.selectedDoctor ? "Choose a procedure..." : "Please select a doctor first"}
              value={fileState.selectedProcedure}
              onChange={handleProcedureChange}
              style={{ width: '100%' }}
              disabled={!fileState.selectedDoctor}
              loading={loadingProcedures}
            >
              {availableProcedures.map((procedure, index) => (
                <Option key={index} value={procedure}>{procedure}</Option>
              ))}
            </Select>
          </div>

          <div className="form-item">
            <label>Surgery Date: <span style={{ color: 'red' }}>*</span></label>
            <DatePicker
              style={{ width: '100%' }}
              placeholder="Select surgery date"
              format="YYYY-MM-DD"
              value={fileState.surgeryDate}
              onChange={(date) => setFileState(prev => ({ ...prev, surgeryDate: date }))}
              disabledDate={(current) => {
                // Disable past dates
                return current && current < dayjs().startOf('day');
              }}
            />
          </div>

          <div className="form-item">
            <label>Upload File:</label>
            <Upload
              key={modalKey}
              beforeUpload={() => false}
              onChange={handleFileChange}
              accept=".pdf,.xlsx,.csv,.txt"
              showUploadList={true}
            >
              <Button icon={<UploadOutlined />}>Choose File</Button>
            </Upload>
          </div>

                     <div className="form-item">
             <Switch
               checked={fileState.enableOnDemand}
               onChange={handleToggleChange}
             />
             <span style={{ marginLeft: '8px' }}>Enable on demand</span>
           </div>

                     {/* Error Display */}
           {fileState.uploadError && (
             <div className="form-item" style={{ marginTop: 16 }}>
               <div style={{ 
                 padding: '12px 16px', 
                 backgroundColor: '#fff2f0', 
                 border: '1px solid #ffccc7', 
                 borderRadius: '8px',
                 color: '#cf1322',
                 whiteSpace: 'pre-line'
               }}>
                 <strong>Upload Error:</strong><br />
                 {fileState.uploadError}
               </div>
             </div>
           )}
        </div>
      </Modal>

      {/* Resubmit Modal */}
      <Modal
        title="Resubmit Demographics Record"
        open={isResubmitModalVisible}
        onOk={handleResubmitSubmit}
        onCancel={handleResubmitCancel}
        width={600}
        confirmLoading={resubmitFileState.isUploading}
        okText="Resubmit"
        cancelText="Cancel"
      >
        <div className="modal-form">
          <div style={{ 
            padding: '12px 16px', 
            backgroundColor: '#f6ffed', 
            border: '1px solid #b7eb8f', 
            borderRadius: '8px',
            marginBottom: '16px',
            color: '#389e0d'
          }}>
            <strong>Resubmit Confirmation:</strong><br />
            You are about to resubmit record ID: {resubmitRecord?.id}. This will update the existing record with new data. You can optionally upload a new file or keep the current one. Please review and confirm your changes.
          </div>

          <div className="form-item">
            <label>Select Doctor:</label>
            <Select
              placeholder="Choose a doctor..."
              value={resubmitFileState.selectedDoctor}
              onChange={(value) => setResubmitFileState(prev => ({ ...prev, selectedDoctor: value }))}
              style={{ width: '100%' }}
              loading={loadingDoctors}
            >
              {doctors.map((doctor, index) => (
                <Option key={index} value={doctor.employeeId}>{doctor.name}</Option>
              ))}
            </Select>
          </div>

          <div className="form-item">
            <label>Select Procedure:</label>
            <Select
              placeholder={resubmitFileState.selectedDoctor ? "Choose a procedure..." : "Please select a doctor first"}
              value={resubmitFileState.selectedProcedure}
              onChange={(value) => setResubmitFileState(prev => ({ ...prev, selectedProcedure: value }))}
              style={{ width: '100%' }}
              disabled={!resubmitFileState.selectedDoctor}
              loading={loadingProcedures}
            >
              {availableProcedures.map((procedure, index) => (
                <Option key={index} value={procedure}>{procedure}</Option>
              ))}
            </Select>
          </div>

          <div className="form-item">
            <label>Surgery Date: <span style={{ color: 'red' }}>*</span></label>
            <DatePicker
              style={{ width: '100%' }}
              placeholder="Select surgery date"
              format="YYYY-MM-DD"
              value={resubmitFileState.surgeryDate}
              onChange={(date) => setResubmitFileState(prev => ({ ...prev, surgeryDate: date }))}
              disabledDate={(current) => {
                // Disable past dates
                return current && current < dayjs().startOf('day');
              }}
            />
          </div>

          <div className="form-item">
            <label>Current File:</label>
            <div style={{ 
              padding: '8px 12px', 
              backgroundColor: '#f5f5f5', 
              border: '1px solid #d9d9d9', 
              borderRadius: '8px',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FileTextOutlined style={{ color: '#1890ff' }} />
                <span>{resubmitRecord?.filename}</span>
              </div>
              <Button
                type="link"
                size="small"
                onClick={() => handleViewPDF(resubmitRecord?.id || '')}
                style={{ padding: '0 4px' }}
              >
                View
              </Button>
            </div>
          </div>

          <div className="form-item">
            <label>Upload New File (Optional):</label>
            <Upload
              beforeUpload={() => false}
              onChange={(info) => {
                const file = info.file.originFileObj || info.file;
                if (file) {
                  const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv'];
                  if (file.type && allowedTypes.includes(file.type)) {
                    setResubmitFileState(prev => ({ ...prev, selectedFile: file as File, uploadError: null }));
                  } else {
                    message.error('Invalid file type. Only PDF, Excel, and CSV files are allowed.');
                    setResubmitFileState(prev => ({ ...prev, selectedFile: null, uploadError: null }));
                  }
                }
              }}
              accept=".pdf,.xlsx,.csv,.txt"
              showUploadList={true}
            >
              <Button icon={<UploadOutlined />}>Choose New File</Button>
            </Upload>
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px',
              fontStyle: 'italic'
            }}>
              Leave empty to keep the current file
            </div>
          </div>

          <div className="form-item">
            <Switch
              checked={resubmitFileState.enableOnDemand}
              onChange={(checked) => setResubmitFileState(prev => ({ ...prev, enableOnDemand: checked }))}
            />
            <span style={{ marginLeft: '8px' }}>Enable on demand</span>
          </div>

          {/* Error Display */}
          {resubmitFileState.uploadError && (
            <div className="form-item" style={{ marginTop: 16 }}>
              <div style={{ 
                padding: '12px 16px', 
                backgroundColor: '#fff2f0', 
                border: '1px solid #ffccc7', 
                borderRadius: '8px',
                color: '#cf1322',
                whiteSpace: 'pre-line'
              }}>
                <strong>Upload Error:</strong><br />
                {resubmitFileState.uploadError}
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Notes Modal */}
      <Modal
        title={`Order Notes for #${selectedRecord?.id || ''}`}
        open={isNotesModalVisible}
        onCancel={handleCloseNotesModal}
        footer={null}
        width={600}
        centered
      >
        {selectedRecord && !isNotesReadOnly(selectedRecord) && (
          <div style={{ marginBottom: '16px' }}>
            <Input.TextArea
              placeholder="Add a new note..."
              value={newNoteText}
              onChange={(e) => setNewNoteText(e.target.value)}
              rows={3}
              disabled={addingNote}
              style={{ marginBottom: '8px' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <Button
                type="primary"
                onClick={handleAddNote}
                disabled={!newNoteText.trim() || addingNote}
                loading={addingNote}
                size="small"
              >
                Save Note
              </Button>
              <Button
                onClick={() => setNewNoteText('')}
                disabled={addingNote}
                size="small"
              >
                Clear
              </Button>
            </div>
            {addingNote && (
              <div style={{ textAlign: 'center', marginTop: '8px' }}>
                <Spin size="small" /> Adding note...
              </div>
            )}
          </div>
        )}

        {selectedRecord && isNotesReadOnly(selectedRecord) && (
          <div style={{ 
            marginBottom: '16px', 
            padding: '12px', 
            backgroundColor: '#f6ffed', 
            border: '1px solid #b7eb8f', 
            borderRadius: '6px',
            color: '#389e0d'
          }}>
            <strong>Read-Only Mode:</strong> This order is completed or cancelled. Notes can only be viewed, not modified.
          </div>
        )}

        {/* Role Information Banner */}
        <div style={{ 
          marginBottom: '16px', 
          padding: '10px', 
          backgroundColor: '#e6f7ff', 
          border: '1px solid #91d5ff', 
          borderRadius: '6px',
          fontSize: '12px',
          color: '#0050b3'
        }}>
          <strong>Note:</strong> {user?.role === 'admin' ? 'Admins can delete any note' : 'You can only delete notes you created'}
        </div>

        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {notesLoading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Spin size="large" />
            </div>
          ) : notes.length === 0 ? (
            <Empty 
              description="No notes yet" 
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <List
              dataSource={notes}
              renderItem={(note) => (
                <List.Item
                  actions={[
                    // Only show delete button if user can delete this note and order is not read-only
                    selectedRecord && !isNotesReadOnly(selectedRecord) && canDeleteNote(note) && (
                      <Button
                        key="delete"
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          Modal.confirm({
                            title: 'Delete Note',
                            content: 'Are you sure you want to delete this note?',
                            okText: 'Yes',
                            cancelText: 'No',
                            onOk: () => deleteNote(note._id, selectedRecord?.orderId || '')
                          });
                        }}
                        size="small"
                      />
                    )
                  ].filter(Boolean)}
                >
                  <List.Item.Meta
                    title={
                      <div style={{ fontSize: '14px', fontWeight: 'normal' }}>
                        {note.text}
                      </div>
                    }
                    description={
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Added by {note.addedBy}
                        {note.addedByRole && (
                          <Tag 
                            color={
                              note.addedByRole === 'admin' ? 'red' :
                              note.addedByRole === 'practitioner' ? 'blue' :
                              note.addedByRole === 'manufacturer' ? 'green' :
                              note.addedByRole === 'insurance' ? 'orange' :
                              'default'
                            }
                            style={{ marginLeft: '8px', fontSize: '11px' }}
                          >
                            {note.addedByRole.charAt(0).toUpperCase() + note.addedByRole.slice(1)}
                          </Tag>
                        )}
                        {' • '}{new Date(note.createdAt).toLocaleString()}
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </div>
      </Modal>

      {/* Insurance Upload Modal */}
      <Modal
        title={`Upload Insurance File - Record #${selectedRecordForInsurance?.id || ''}`}
        open={isInsuranceUploadModalVisible}
        onOk={handleInsuranceFileUpload}
        onCancel={() => {
          setIsInsuranceUploadModalVisible(false);
          setSelectedRecordForInsurance(null);
          setInsuranceFileState({
            selectedFile: null,
            isUploading: false,
            uploadError: null
          });
        }}
        width={500}
        confirmLoading={insuranceFileState.isUploading}
        okText="Upload"
        cancelText="Cancel"
      >
        <div className="modal-form">
          <div style={{ marginBottom: '16px' }}>
            <p><strong>Patient:</strong> {selectedRecordForInsurance ? 
              [selectedRecordForInsurance.firstName, selectedRecordForInsurance.middleName, selectedRecordForInsurance.lastName]
                .filter(name => name && name.trim())
                .join(' ') : ''
            }</p>
            <p><strong>Doctor:</strong> {selectedRecordForInsurance?.doctorName}</p>
            <p><strong>Procedure:</strong> {selectedRecordForInsurance?.procedureName}</p>
          </div>

          <div className="form-item">
            <label>Upload Insurance File (PDF only):</label>
            <Upload
              beforeUpload={() => false}
              onChange={(info) => {
                const file = info.file.originFileObj || info.file;
                if (file) {
                  if (file.type === 'application/pdf') {
                    setInsuranceFileState(prev => ({ 
                      ...prev, 
                      selectedFile: file as File, 
                      uploadError: null 
                    }));
                  } else {
                    message.error('Only PDF files are allowed for insurance verification');
                    setInsuranceFileState(prev => ({ 
                      ...prev, 
                      selectedFile: null, 
                      uploadError: 'Only PDF files are allowed' 
                    }));
                  }
                }
              }}
              accept=".pdf"
              showUploadList={true}
            >
              <Button icon={<UploadOutlined />}>Choose PDF File</Button>
            </Upload>
          </div>

          {/* Error Display */}
          {insuranceFileState.uploadError && (
            <div className="form-item" style={{ marginTop: 16 }}>
              <div style={{ 
                padding: '12px 16px', 
                backgroundColor: '#fff2f0', 
                border: '1px solid #ffccc7', 
                borderRadius: '8px',
                color: '#cf1322'
              }}>
                <strong>Upload Error:</strong><br />
                {insuranceFileState.uploadError}
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* View Order Modal */}
      <Modal
        title="Order Details"
        open={viewOrderModalVisible}
        onCancel={() => {
          setViewOrderModalVisible(false);
          setSelectedOrder(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setViewOrderModalVisible(false);
            setSelectedOrder(null);
          }}>
            Close
          </Button>
        ]}
        width={800}
      >
        {selectedOrder && (
          <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <div style={{ marginBottom: '16px' }}>
              <h3>Order Information</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <strong>Order ID:</strong> {selectedOrder.orderId}
                </div>
                <div>
                  <strong>Status:</strong> 
                  <Tag color={
                    selectedOrder.orderStatus === 'Delivered' ? 'green' :
                    selectedOrder.orderStatus === 'Completed' ? 'green' :
                    selectedOrder.orderStatus === 'Cancelled' ? 'red' :
                    selectedOrder.orderStatus === 'YetToBeShipped' ? 'orange' :
                    selectedOrder.orderStatus === 'ReadyToBeShipped' ? 'blue' :
                    selectedOrder.orderStatus === 'Shipped' ? 'purple' :
                    selectedOrder.orderStatus === 'ReadyToBill' ? 'purple' :
                    selectedOrder.orderStatus === 'PaymentDue' ? 'orange' :
                    selectedOrder.orderStatus === 'PaymentDenied' ? 'red' :
                    selectedOrder.orderStatus === 'PaymentReceived' ? 'green' : 'default'
                  }>
                    {selectedOrder.orderStatus}
                  </Tag>
                </div>
                <div>
                  <strong>Demographics ID:</strong> {selectedOrder.demographicsId}
                </div>
                <div>
                  <strong>Created:</strong> {new Date(selectedOrder.createdAt).toLocaleString()}
                </div>
                <div>
                  <strong>Last Modified:</strong> {new Date(selectedOrder.lastModifiedDate).toLocaleString()}
                </div>
              </div>
            </div>

            {selectedOrder.orderAddress && (
              <div style={{ marginBottom: '16px' }}>
                <h3>Order Address</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <strong>Address:</strong> {selectedOrder.orderAddress.address || 'N/A'}
                  </div>
                  <div>
                    <strong>City:</strong> {selectedOrder.orderAddress.city || 'N/A'}
                  </div>
                  <div>
                    <strong>State:</strong> {selectedOrder.orderAddress.state || 'N/A'}
                  </div>
                  <div>
                    <strong>Zip:</strong> {selectedOrder.orderAddress.zip || 'N/A'}
                  </div>
                </div>
              </div>
            )}

            {selectedOrder.orderItems && selectedOrder.orderItems.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <h3>Order Items</h3>
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {selectedOrder.orderItems.map((item: any, index: number) => (
                    <Card key={index} size="small" style={{ marginBottom: '8px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <div><strong>Type:</strong> {item.type}</div>
                        <div><strong>Product:</strong> {item.item?.productName}</div>
                        <div><strong>Product ID:</strong> {item.item?.productId}</div>
                        <div><strong>Category:</strong> {item.item?.productCategory}</div>
                        <div><strong>Price:</strong> ${item.item?.price}</div>
                        <div><strong>Quantity:</strong> {item.quantity}</div>
                        <div><strong>Place of Service:</strong> {item.placeOfService}</div>
                        <div><strong>Rendering Provider:</strong> {item.renderingProvider}</div>
                        <div><strong>Frequency:</strong> {item.frequency}</div>
                        <div><strong>Days:</strong> {item.days}</div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {selectedOrder.notes && selectedOrder.notes.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <h3>Order Notes</h3>
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {selectedOrder.notes.map((note: any, index: number) => (
                    <div key={index} style={{ 
                      padding: '8px', 
                      marginBottom: '8px', 
                      backgroundColor: '#f5f5f5', 
                      borderRadius: '4px' 
                    }}>
                      <div><strong>{note.addedBy}:</strong> {note.text}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {new Date(note.timestamp).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Status Update Modal */}
      <Modal
        title="Update Order Status"
        open={isStatusUpdateModalVisible}
        onCancel={() => {
          setIsStatusUpdateModalVisible(false);
          setSelectedOrderForStatusUpdate(null);
          setSelectedStatusForUpdate('');
          statusUpdateForm.resetFields();
          // Clear additional fields
          statusUpdateForm.setFieldsValue({
            printingSlipBase64: null,
            printingSlipFilename: null,
            printingSlipContentType: null
          });
        }}
        footer={null}
        width={600}
      >
        <Form
          form={statusUpdateForm}
          layout="vertical"
          onFinish={handleStatusUpdateSubmit}
        >
          <Form.Item
            name="newStatus"
            label="New Status"
            rules={[{ required: true, message: 'Please select a status' }]}
          >
            <Select placeholder="Select status" onChange={(value) => {
              setSelectedStatusForUpdate(value);
              // Reset form fields when status changes
              statusUpdateForm.setFieldsValue({ 
                trackingId: '', 
                deliveryDate: null,
                printingSlip: null,
                printingSlipBase64: null,
                printingSlipFilename: null,
                printingSlipContentType: null
              });
            }}>
              {/* Manufacturer options: Based on workflow */}
              {user?.role === 'manufacturer' && selectedOrderForStatusUpdate?.orderStatus === 'YetToBeShipped' && (
                <Select.Option value="Shipped">Shipped</Select.Option>
              )}
              
              {user?.role === 'manufacturer' && selectedOrderForStatusUpdate?.orderStatus === 'Shipped' && (
                <Select.Option value="Delivered">Delivered</Select.Option>
              )}
              
              {/* Practitioner options: Ready to Bill (for Delivered orders) */}
              {user?.role !== 'manufacturer' && selectedOrderForStatusUpdate?.orderStatus === 'Delivered' && (
                <Select.Option value="ReadyToBill">Ready to Bill</Select.Option>
              )}
              
              {/* Practitioner options: Switch between payment statuses (for Payment Due/Received/Denied orders) */}
              {user?.role !== 'manufacturer' && ['PaymentDue', 'PaymentReceived', 'PaymentDenied'].includes(selectedOrderForStatusUpdate?.orderStatus) && (
                <>
                  {selectedOrderForStatusUpdate?.orderStatus !== 'PaymentDue' && (
                    <Select.Option value="PaymentDue">Payment Due</Select.Option>
                  )}
                  {selectedOrderForStatusUpdate?.orderStatus !== 'PaymentReceived' && (
                    <Select.Option value="PaymentReceived">Payment Received</Select.Option>
                  )}
                  {selectedOrderForStatusUpdate?.orderStatus !== 'PaymentDenied' && (
                    <Select.Option value="PaymentDenied">Payment Denied</Select.Option>
                  )}
                </>
              )}
            </Select>
          </Form.Item>

          {selectedStatusForUpdate === 'Shipped' && (
            <>
              <Form.Item
                name="deliveryPartnerId"
                label="Delivery Partner"
                rules={[
                  { required: true, message: 'Please select a delivery partner' }
                ]}
              >
                <Select 
                  placeholder="Select delivery partner"
                  loading={loadingDeliveryPartners}
                >
                  {deliveryPartners.map((partner) => (
                    <Select.Option key={partner._id} value={partner._id}>
                      {partner.deliveryPartnerName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="trackingId"
                label="Tracking ID"
                rules={[
                  { required: true, message: 'Tracking ID is required for Shipped status' },
                  { min: 1, message: 'Tracking ID cannot be empty' }
                ]}
              >
                <Input placeholder="Enter tracking ID" />
              </Form.Item>
            </>
          )}

          {selectedStatusForUpdate === 'Delivered' && (
            <>
              <Form.Item
                name="deliveryDate"
                label="Delivery Date"
                rules={[
                  { required: true, message: 'Delivery date is required for Delivered status' }
                ]}
              >
                <DatePicker 
                  style={{ width: '100%' }} 
                  placeholder="Select delivery date"
                  format="YYYY-MM-DD"
                />
              </Form.Item>
              
              {/* Hidden field for base64 data */}
              <Form.Item name="printingSlipBase64" style={{ display: 'none' }}>
                <Input />
              </Form.Item>
              
              <Form.Item
                name="printingSlip"
                label="Printing Slip"
                rules={[
                  { 
                    required: true, 
                    message: 'Printing slip is required for Delivered status',
                    validator: (_, value) => {
                      const base64Value = statusUpdateForm.getFieldValue('printingSlipBase64');
                      console.log('🔍 Printing slip validation - base64Value:', base64Value ? 'present' : 'missing');
                      if (!base64Value) {
                        return Promise.reject(new Error('Please upload a printing slip'));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
                valuePropName="fileList"
                getValueFromEvent={(e) => {
                  if (Array.isArray(e)) {
                    return e;
                  }
                  return e?.fileList;
                }}
                extra="Upload a PDF file to confirm successful order processing"
              >
                <Upload
                  beforeUpload={(file) => {
                    // Convert file to base64 for upload
                    const reader = new FileReader();
                    reader.onload = (e) => {
                      const base64 = e.target?.result as string;
                      const base64Data = base64.split(',')[1]; // Remove data:application/pdf;base64, prefix
                      
                      console.log('📄 File uploaded - name:', file.name, 'base64 length:', base64Data?.length);
                      
                      // Set all the required fields
                      statusUpdateForm.setFieldsValue({ 
                        printingSlipBase64: base64Data,
                        printingSlipFilename: file.name,
                        printingSlipContentType: file.type
                      });
                      
                      // Trigger validation for both fields
                      statusUpdateForm.validateFields(['printingSlip', 'printingSlipBase64']);
                    };
                    reader.readAsDataURL(file);
                    return false; // Prevent auto upload
                  }}
                  accept=".pdf"
                  maxCount={1}
                  showUploadList={{
                    showRemoveIcon: true,
                    showDownloadIcon: false,
                    showPreviewIcon: false
                  }}
                  onRemove={() => {
                    statusUpdateForm.setFieldsValue({
                      printingSlipBase64: null,
                      printingSlipFilename: null,
                      printingSlipContentType: null
                    });
                    // Trigger validation after removal
                    statusUpdateForm.validateFields(['printingSlip', 'printingSlipBase64']);
                    return true;
                  }}
                >
                  <Button icon={<UploadOutlined />}>Upload Printing Slip (PDF)</Button>
                </Upload>
              </Form.Item>
            </>
          )}

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button 
              onClick={() => {
                setIsStatusUpdateModalVisible(false);
                setSelectedOrderForStatusUpdate(null);
                setSelectedStatusForUpdate('');
                statusUpdateForm.resetFields();
              }}
              style={{ marginRight: 8 }}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">
              Update Status
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Payment Modal */}
      <Modal
        title={selectedOrderForPayment?.paymentInfo ? "Update Payment Information" : "Add Payment Information"}
        open={isPaymentModalVisible}
        onCancel={() => {
          setIsPaymentModalVisible(false);
          setSelectedOrderForPayment(null);
          paymentForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={paymentForm}
          layout="vertical"
          onFinish={handlePaymentSubmit}
        >
          <Form.Item
            name="primaryAmount"
            label="Primary Amount ($)"
            initialValue={0}
            rules={[
              { required: true, message: 'Please enter primary amount' },
              { 
                type: 'number', 
                min: 0, 
                message: 'Amount must be 0 or greater',
                transform: (value) => Number(value)
              }
            ]}
          >
            <Input
              type="number"
              placeholder="0.00"
              prefix="$"
              min={0}
              step={0.01}
            />
          </Form.Item>

          <Form.Item
            name="secondaryAmount"
            label="Secondary Amount ($)"
            initialValue={0}
            rules={[
              { 
                type: 'number', 
                min: 0, 
                message: 'Amount must be 0 or greater',
                transform: (value) => Number(value)
              }
            ]}
          >
            <Input
              type="number"
              placeholder="0.00"
              prefix="$"
              min={0}
              step={0.01}
            />
          </Form.Item>

          <Form.Item
            name="patientAmount"
            label="Patient Amount ($)"
            initialValue={0}
            rules={[
              { 
                type: 'number', 
                min: 0, 
                message: 'Amount must be 0 or greater',
                transform: (value) => Number(value)
              }
            ]}
          >
            <Input
              type="number"
              placeholder="0.00"
              prefix="$"
              min={0}
              step={0.01}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button 
              onClick={() => {
                setIsPaymentModalVisible(false);
                setSelectedOrderForPayment(null);
                paymentForm.resetFields();
              }}
              style={{ marginRight: 8 }}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">
              {selectedOrderForPayment?.paymentInfo ? "Update Payment" : "Submit Payment"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default OrdersPage;
